import { useDarkMode } from "@/hooks/useDarkMode";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  mapCenterAtom,
  mapDetailOriginAtom,
  mobileListViewAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  savedPlaceSortByToViewAtom,
  hoveredVenueIdAtom,
  viewModeAtom,
  pendingGooglePlaceIdAtom,
  mapViewportAtom,
  fitMapToResultsAtom,
} from "@/state/atoms";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import {
  APIProvider,
  Map as VisGlMap,
  AdvancedMarker,
  InfoWindow,
  useMap,
  type MapMouseEvent as VisGlMapMouseEvent,
} from "@vis.gl/react-google-maps";
import {
  defaultRegion,
  darkModeStyles,
  lightModeStyles,
  enableAutoZoom,
  enableGooglePoiClick,
  enableAdvancedMarkers,
  googleMapKey,
  googleMapId,
  googleMapIdPoi,
} from "common/config";
import {
  AdvancedPinContent,
  SIZE_DEFAULT as PIN_SIZE_DEFAULT,
  SIZE_SELECTED as PIN_SIZE_SELECTED,
  PIN_ASPECT,
  NAME_HALO,
  NAME_TEXT_COLOR,
  CATEGORY_TEXT_COLOR,
  getPinCentreTop,
} from "@/components/Map/AdvancedPinContent";
import { PoiContributionCard } from "@/components/Map/PoiContributionCard";
import { MapModeToggle } from "@/components/Map/MapModeToggle";
import { useMapMode } from "@/components/Map/useMapMode";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { createPlaceFromGoogle } from "common/apis";
import { getPinIcon as resolveVenuePinType } from "common/utils/maps";
import { Storage } from "common/storage";
import { trackEvent } from "common/analytics";
import { pinTypeForVenue } from "common/utils";
import {
  useSearchPageParams,
  useHomeVenues,
  useI18N,
  useMapSettings,
  useSavedPlaces,
  useAuth,
} from "common/hooks";
import { Venue, VenuePinType } from "common/types";
import { calculateZoomFromDistance } from "common/apis/search";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useMatchRoute } from "@tanstack/react-router";

import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";

import YouAre from "@/assets/pins/youAre.svg";
import VenueCard from "@/components/VenueCard/VenueCard";
import { FiMapPin } from "react-icons/fi";
import { cn } from "@/lib/utils";
import { TbCurrentLocation } from "react-icons/tb";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { XIcon } from "@phosphor-icons/react";
import Loader from "@/components/common/Loader";
import ModalWithAction from "@/components/common/ModalWithAction";
import { StandardButton } from "@/components/common/StandardButton";
import Modal from "react-responsive-modal";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";

/**
 * Captures the vis.gl map instance and forwards it to the parent component's
 * mapRef state so all existing mapRef.setZoom / mapRef.getCenter calls work
 * unchanged in the Advanced Markers path.
 */
function MapRefCapture({ onMap }: { onMap: (map: google.maps.Map) => void }) {
  const map = useMap();
  React.useEffect(() => {
    if (map) onMap(map);
  }, [map, onMap]);
  return null;
}

// Stable reference so vis.gl InfoWindow never sees a changed pixelOffset between renders.
const SNIPPET_PIXEL_OFFSET: [number, number] = [0, -50];

// Clustering / labels tuning (Sociability mode).
const CLUSTER_RADIUS = 80; // px: markers within this distance merge into a cluster
const CLUSTER_MAX_ZOOM = 17; // above this zoom clustering is off (pins are individual)
// Names render only once clusters have dissolved, so a label never floats over a
// cluster bubble (no orphan labels). Google's collision then declutters them.
const LABEL_MIN_ZOOM = CLUSTER_MAX_ZOOM + 1;
// Non-clustered modes (Explore / All places) have no cluster bubbles, so names
// can appear earlier; Google's collision still thins them as you zoom.
const UNCLUSTERED_LABEL_MIN_ZOOM = 18;

// Sociability-themed (orange) cluster bubble, replacing markerclusterer's default blue.
const orangeClusterRenderer = {
  render: ({
    count,
    position,
  }: {
    count: number;
    position: google.maps.LatLng | google.maps.LatLngLiteral;
  }) => {
    const size = count < 10 ? 40 : count < 100 ? 48 : 56;
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:9999px;background:rgba(255,106,0,0.25);`;
    const inner = document.createElement("div");
    inner.style.cssText = `display:flex;align-items:center;justify-content:center;width:${size * 0.7}px;height:${size * 0.7}px;border-radius:9999px;background:#FF6A00;color:#fff;font-weight:700;font-size:${size * 0.32}px;line-height:1;box-shadow:0 1px 3px rgba(0,0,0,0.3);`;
    inner.textContent = String(count);
    wrapper.appendChild(inner);
    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: wrapper,
      // Above every pin/label priority (selected pins use 1_000_000) so an
      // overlapping marker can never sit on top and swallow the cluster click.
      zIndex: 2_000_000 + count,
      // Required for the library's gmp-click (zoom-in) listener to fire.
      gmpClickable: true,
    });
  },
};

function BasicMapView(props: {
  query: string;
  hidden?: boolean;
  currentVenueTypePage?: number;
  mapSource?: string;
  locations?: Venue[];
}) {
  const { currentVenueTypePage = 0, locations: locationsOverride } = props;
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [savedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [savedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);

  const i18n = useI18N();
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const { mapSettings } = useMapSettings();

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [tooltipVenue, setTooltipVenue] = useState<Venue | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [snippetPosition, setSnippetPosition] = useState<{
    offset: { x: number; y: number };
    className: string;
  } | null>(null);
  const [snippetOpen, setSnippetOpen] = useState<boolean>(false); // Track if snippet is actively displayed
  // TODO: Panning state commented out since we're not using map panning
  // const [isPanning, setIsPanning] = useState(false);
  // Cooldown to suppress auto-zoom right after a user-initiated map move
  const userMovedMapAtRef = useRef<number>(0);

  // Track if we've set the initial user location to prevent duplicate API calls
  // When navigating to search page with coordinates already set, we don't want to
  // immediately update userlatitude/userlongitude and trigger another API call
  const hasSetInitialUserLocationRef = useRef(false);
  // When user clicks "Go to current location", the next userLocation update should
  // force a full reset of search center (latitude/longitude + unixTimestamp)
  const goToUserLocationRequestedRef = useRef(false);

  // Track if map has completed initial load to prevent onMapMoved from firing during map initialization
  // This prevents the map from overwriting synced location params when the idle event fires on initial map load
  const mapHasCompletedInitialLoadRef = useRef(false);
  // Track the last location params that the map was initialized with
  // Only allow onMapMoved to update location if the current params match the initialized params
  const lastInitializedParamsRef = useRef<{
    lat: string | null;
    lng: string | null;
  }>({ lat: null, lng: null });
  // Zoom at the time of the last reload — used to measure accumulated zoom delta
  // (not updated on every idle, only when a reload is triggered or on init)
  const lastReloadedZoomRef = useRef<number | null>(null);
  // Debounce timer for progressive search — clears rapid zoom steps and mid-pan idles
  const progressiveSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { selectedVenue, setSelectedVenue } = useSelectedVenue();

  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const [, setDetailOrigin] = useAtom(mapDetailOriginAtom);
  const [hoveredVenueId, setHoveredVenueId] = useAtom(hoveredVenueIdAtom);

  const { isMapsLoaded } = useLoadGoogleMaps();

  const [mapCenter, setMapCenter] = useAtom(mapCenterAtom);
  const setMapViewport = useSetAtom(mapViewportAtom);
  const pendingGooglePlaceId = useAtomValue(pendingGooglePlaceIdAtom);
  const setPendingGooglePlaceId = useSetAtom(pendingGooglePlaceIdAtom);
  const fitMapToResults = useAtomValue(fitMapToResultsAtom);
  
  const isDark = useDarkMode();
  const { user, isMapper } = useAuth();
  const [viewMode] = useAtom(viewModeAtom);
  const isMapperViewActive = isMapper && viewMode === "mapper";

  // CheckIcon if we're on the /search route to conditionally skip certain effects
  const matchRoute = useMatchRoute();
  const isSearchRoute = matchRoute({ to: "/search" });

  const { data, refetch } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];

  const markerRefs = useRef(new Map());

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [locationForSavedPlaces, setLocationForSavedPlaces] = useState(0);
  const [listNameCreated, setListNameCreated] = useState("");

  // Venues resolved from clicking native Google POIs. Persisted on the map until
  // the next search so re-clicking an already-resolved POI doesn't refetch.
  const [resolvedPois, setResolvedPois] = useState<Venue[]>([]);
  // Highlights the native Google POI icon the user last clicked (wherever
  // native POIs are visible/clickable: All-places mode, venue-detail map),
  // so it reads as "selected" the same way our own pins do.
  const [selectedPoiPosition, setSelectedPoiPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // Message card at the clicked point for unsupported / error cases.
  const [poiMessage, setPoiMessage] = useState<{
    lat: number;
    lng: number;
    text: string;
  } | null>(null);
  // Lightweight "resolving a tapped place" indicator (non-blocking pill, not the
  // full-screen loader) while place-from-google runs.
  const [poiResolving, setPoiResolving] = useState(false);
  // Opt-in contribution flow for a clicked Google POI that's supported but not yet
  // in our DB (All-places mode only): intro -> options (multi-select) -> create.
  // "unsupported" is a terminal message for categories we don't cover.
  const [poiContribution, setPoiContribution] = useState<{
    step: "intro" | "options" | "unsupported";
    placeId: string;
    lat: number;
    lng: number;
    name?: string;
    // When set, the venue already exists in our DB (a grey pin with no
    // accessibility data). "Continue" opens it instead of creating a new one.
    venue?: Venue;
    selected: string[];
    creating: boolean;
  } | null>(null);
  // place_ids in-flight, to dedupe calls from concurrent event sources (map click + selectGooglePlace).
  const resolvingPlaceIds = useRef<Set<string>>(new Set());

  // Listen for custom force zoom event
  useEffect(() => {
    if (mapRef) {
      const handleForceZoom = (
        event: CustomEvent<{ zoom: number; lat: number; lng: number }>,
      ) => {
        const { zoom, lat, lng } = event.detail;
        console.log("Force zoom event received:", zoom, lat, lng);

        // Set the map center and zoom level
        mapRef.setCenter({ lat, lng });
        mapRef.setZoom(zoom);
      };

      window.addEventListener("forceMapZoom", handleForceZoom as EventListener);

      return () => {
        window.removeEventListener(
          "forceMapZoom",
          handleForceZoom as EventListener,
        );
      };
    }
  }, [mapRef]);

  // Sync saved places modal mode with saved places data
  useEffect(() => {
    if (savedPlaces?.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  }, [savedPlaces?.length, user]);

  // Close snippet when map is moved or zoomed
  useEffect(() => {
    if (mapRef) {
      const closeSnippet = () => {
        setActiveMarker(null);
        setSnippetPosition(null);
        setTooltipVenue(null);
        setHoveredMarkerId(null);
        setSnippetOpen(false);
      };

      // Add listeners for map interactions that should close the snippet
      const dragStartListener = mapRef.addListener("dragstart", closeSnippet);
      const zoomChangedListener = mapRef.addListener(
        "zoom_changed",
        closeSnippet,
      );

      // Cleanup listeners on unmount
      return () => {
        if (dragStartListener) {
          google.maps.event.removeListener(dragStartListener);
        }
        if (zoomChangedListener) {
          google.maps.event.removeListener(zoomChangedListener);
        }
      };
    }
  }, [mapRef]);

  const handleSetLocation = async () => {
    //set user location
    // console.log("inside handleSetLocation");
    setIsLocationModalOpen(false);
    return;
  };

  // Function to show snippet only (no venue loading)
  const handleShowSnippet = (marker: string) => {
    if (marker === activeMarker && snippetOpen) {
      return;
    }

    // Calculate and cache snippet position FIRST before any state changes
    const venue = locations.find((l) => l.id === marker);
    if (!venue) return;

    const pos = getSnippetPosition(venue);

    // Now update all states at once to minimize re-renders
    setTooltipVenue(null);
    setHoveredMarkerId(marker);
    setHoveredVenueId(marker);
    setSnippetPosition(pos);
    setActiveMarker(marker);
    setSnippetOpen(true); // Mark snippet as open
  };

  // Function to load venue in left panel (called from snippet click)
  const handleLoadVenue = (location: Venue) => {
    // Complex venue: open in new tab (matches list click behaviour)
    if (
      location.business?.templateId != null &&
      location.business?.templateId > 1 &&
      location.business?.publicUrl &&
      location.business.publicUrl !== ""
    ) {
      window.open(`/venuedetails/${location.business.publicUrl}`, "_blank");
      handleCloseSnippet(); // close the snippet so the map is left clean
      return;
    }

    // Basic venue: load in side panel. The detail lives in the list container,
    // so we flip to it — but remember the user came from the map so the back
    // action can return them to the map view.
    setDetailOrigin("map");
    setMobileListView(true);
    setSelectedVenue(location);
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setSnippetOpen(false); // Close snippet
    // Note: Keep hoveredVenueId for the selected venue
  };

  // Save venue handlers
  const handleSaveVenue = (location) => {
    if (!user) {
      console.log("Please login");
      return;
    }
    console.log("in handleSaveVenue", location);
    setLocationForSavedPlaces(location?.business.id);
    setOpenSavedPlacesModal(true);
  };

  const handleSavedPlacesModalClose = () => {
    console.log(
      "Closing Modal handleSavedPlacesModalClose savePlaceListMode=",
      savePlaceListMode,
    );

    setOpenSavedPlacesModal(false);

    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button and all lists
      handleSaveVenueAll();
    } else {
      setLocationForSavedPlaces(0);
      setListNameCreated("");
    }
  };

  const handleSaveVenueAll = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  // Function to close the snippet without loading venue
  const handleCloseSnippet = () => {
    setActiveMarker(null);
    setSnippetPosition(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setHoveredVenueId("");
    setSnippetOpen(false); // Close snippet
  };

  // Dismiss the transient unsupported/error message card. Resolved POI markers
  // persist on the map (closing the snippet is handled by handleCloseSnippet).
  const handleClosePoi = () => {
    setPoiMessage(null);
    setPoiContribution(null);
    setSelectedPoiPosition(null);
  };

  // Handle a native Google POI click. Returns true when handled, so the caller
  // can skip the default clear behaviour.
  // Persist a resolved venue as a POI pin and open its preview card. When `center`
  // is true (e.g. from auto-suggest) the map pans to it first.
  const showResolvedVenue = (venue: Venue, center: boolean) => {
    // For resolved POIs, the pin should reflect accessibility data only: orange when
    // the venue has reviews, otherwise grey. This avoids showing orange for venues
    // that are non-grey solely due to mapper-only states (e.g. notes for mappers),
    // which would otherwise render as orange in the user view.
    const hasAccessibilityData = (venue.reviewsDetailsSubmitted ?? 0) > 0;
    const resolvedVenue = {
      ...venue,
      venuePinType: hasAccessibilityData
        ? resolveVenuePinType(venue)
        : VenuePinType.Grey,
    };
    // A mapped (orange) venue gets its own pin; drop the POI highlight ring so
    // we don't double up on selection cues.
    if (resolvedVenue.venuePinType !== VenuePinType.Grey) {
      setSelectedPoiPosition(null);
    }
    setResolvedPois((prev) =>
      prev.some((v) => v.placeId === resolvedVenue.placeId)
        ? prev
        : [...prev, resolvedVenue],
    );

    // In the venue-detail context the snippet won't render, so load the venue
    // detail directly in one click (same pattern as regular pins).
    if (currentVenueTypePage === 1 || props.mapSource === "venuedetails") {
      if (center && mapRef && resolvedVenue.latitude != null && resolvedVenue.longitude != null) {
        const dest = { lat: resolvedVenue.latitude, lng: resolvedVenue.longitude };
        mapRef.panTo(dest);
        setMapCenter(dest);
      }
      handleLoadVenue(resolvedVenue);
      return;
    }

    const openSnippet = () => {
      setActiveMarker(resolvedVenue.id);
      setSnippetPosition(getSnippetPosition(resolvedVenue));
      setSnippetOpen(true);
    };
    if (
      center &&
      mapRef &&
      resolvedVenue.latitude != null &&
      resolvedVenue.longitude != null
    ) {
      const dest = { lat: resolvedVenue.latitude, lng: resolvedVenue.longitude };
      mapRef.panTo(dest);
      // Keep mapCenterAtom in sync so auto-suggest uses the new map center
      // for distance sorting on the next search. We update it here (not in
      // onMapMoved) to avoid the re-render that was causing snippet misalignment.
      setMapCenter(dest);
      // Delay so the programmatic-move snippet-close listener fires first.
      window.setTimeout(openSnippet, 350);
    } else {
      openSnippet();
    }
  };

  // Resolve a Google place_id into a venue (POI clicks + auto-suggest): show the
  // preview card on success, or a message card on unsupported/error.
  const resolvePlaceAndShow = (
    placeId: string,
    opts: {
      center: boolean;
      messageLat?: number;
      messageLng?: number;
      sessionToken?: string;
    },
  ) => {
    // Already one of our loaded search pins: just show its snippet (no API call).
    const existing = locations.find((l) => l.placeId && l.placeId === placeId);
    if (existing) {
      if (
        opts.center &&
        mapRef &&
        existing.latitude != null &&
        existing.longitude != null
      ) {
        mapRef.panTo({ lat: existing.latitude, lng: existing.longitude });
        window.setTimeout(() => handleShowSnippet(existing.id), 350);
      } else {
        handleShowSnippet(existing.id);
      }
      return;
    }

    // Already resolved earlier this session: reuse it, no refetch.
    const alreadyResolved = resolvedPois.find((v) => v.placeId === placeId);
    if (alreadyResolved) {
      showResolvedVenue(alreadyResolved, opts.center);
      return;
    }

    // Dedupe concurrent requests for the same place_id (map click + selectGooglePlace).
    if (resolvingPlaceIds.current.has(placeId)) return;
    resolvingPlaceIds.current.add(placeId);

    // Show a small non-blocking pill (not the full-screen loader) while fetching.
    setPoiResolving(true);
    createPlaceFromGoogle(placeId, Storage.getCurrentUserToken(), opts.sessionToken)
      .then((result) => {
        if (result.status === "success") {
          showResolvedVenue(result.venue, opts.center);
        } else {
          // For unsupported results from auto-suggest, the backend returns the
          // place coordinates so we can pan the map and anchor the message there.
          const resultLat = result.status === "unsupported" ? result.lat : null;
          const resultLng = result.status === "unsupported" ? result.lng : null;
          const lat = resultLat ?? opts.messageLat ?? mapRef?.getCenter()?.lat();
          const lng = resultLng ?? opts.messageLng ?? mapRef?.getCenter()?.lng();
          if (lat !== undefined && lng !== undefined && lat !== null && lng !== null) {
            if (opts.center && mapRef) {
              mapRef.panTo({ lat, lng });
            }
            setPoiMessage({
              lat,
              lng,
              text:
                result.status === "unsupported"
                  ? i18n.get("poiUnsupportedCategory")
                  : i18n.get("poiCouldNotAdd"),
            });
          }
        }
      })
      .finally(() => {
        resolvingPlaceIds.current.delete(placeId);
        setPoiResolving(false);
      });
  };

  // A grey venue (in our DB but no accessibility data) looks like a not-in-DB
  // place to the user, so invite them to contribute instead of showing an empty
  // snippet. "Continue" opens the existing venue (see handleContributionContinue).
  const showContributionForVenue = (venue: Venue, center: boolean) => {
    // Only register a resolved pin if it isn't already on the map as a result pin.
    const inLocations = locations.some((l) => l.id === venue.id);
    if (!inLocations && venue.placeId) {
      const greyVenue = { ...venue, venuePinType: VenuePinType.Grey };
      setResolvedPois((prev) =>
        prev.some((v) => v.placeId === greyVenue.placeId)
          ? prev
          : [...prev, greyVenue],
      );
    }

    const openCard = () =>
      setPoiContribution({
        step: "intro",
        placeId: venue.placeId ?? "",
        lat: venue.latitude ?? mapRef?.getCenter()?.lat() ?? 0,
        lng: venue.longitude ?? mapRef?.getCenter()?.lng() ?? 0,
        name: venue.name,
        venue,
        selected: [],
        creating: false,
      });

    if (center && mapRef && venue.latitude != null && venue.longitude != null) {
      mapRef.panTo({ lat: venue.latitude, lng: venue.longitude });
      window.setTimeout(openCard, 350);
    } else {
      openCard();
    }
  };

  const isGreyVenue = (venue: Venue) =>
    (venue.reviewsDetailsSubmitted ?? 0) === 0;

  // All-places mode: classify a clicked POI without creating it. In-DB venues
  // with data show the snippet; grey (no-data) and supported-but-new venues open
  // the contribution card; unsupported categories show a terminal message.
  const handlePoiContribution = (
    placeId: string,
    fallbackLat?: number,
    fallbackLng?: number,
    opts?: { center?: boolean; sessionToken?: string },
  ) => {
    const center = opts?.center ?? false;
    setPoiContribution(null);

    const existing = locations.find((l) => l.placeId && l.placeId === placeId);
    if (existing) {
      // Grey pin (no accessibility data) → contribution card; otherwise snippet.
      if (isGreyVenue(existing)) {
        showContributionForVenue(existing, center);
      } else if (
        center &&
        mapRef &&
        existing.latitude != null &&
        existing.longitude != null
      ) {
        mapRef.panTo({ lat: existing.latitude, lng: existing.longitude });
        window.setTimeout(() => handleShowSnippet(existing.id), 350);
      } else {
        handleShowSnippet(existing.id);
      }
      return;
    }
    const alreadyResolved = resolvedPois.find((v) => v.placeId === placeId);
    if (alreadyResolved) {
      if (isGreyVenue(alreadyResolved)) {
        showContributionForVenue(alreadyResolved, center);
      } else {
        showResolvedVenue(alreadyResolved, center);
      }
      return;
    }
    if (resolvingPlaceIds.current.has(placeId)) return;
    resolvingPlaceIds.current.add(placeId);

    setPoiResolving(true);
    createPlaceFromGoogle(
      placeId,
      Storage.getCurrentUserToken(),
      opts?.sessionToken,
      true,
    )
      .then((result) => {
        if (result.status === "success") {
          // Already in our DB. Grey (no accessibility data) → invite the user to
          // contribute, same as a not-in-DB place. Otherwise behave like a pin.
          if (isGreyVenue(result.venue)) {
            showContributionForVenue(result.venue, center);
          } else {
            showResolvedVenue(result.venue, center);
          }
          return;
        }
        const lat = (result.status === "supported" || result.status === "unsupported"
          ? result.lat
          : null) ?? fallbackLat ?? mapRef?.getCenter()?.lat();
        const lng = (result.status === "supported" || result.status === "unsupported"
          ? result.lng
          : null) ?? fallbackLng ?? mapRef?.getCenter()?.lng();
        if (lat == null || lng == null) return;

        // Centre the POI so the anchored card (desktop) / bottom sheet (mobile)
        // stays fully on screen, mirroring the venue snippet's auto-pan.
        if (
          mapRef &&
          (result.status === "supported" || result.status === "unsupported")
        ) {
          mapRef.panTo({ lat, lng });
        }

        if (result.status === "supported") {
          setPoiContribution({
            step: "intro",
            placeId,
            lat,
            lng,
            name: result.name,
            selected: [],
            creating: false,
          });
        } else if (result.status === "unsupported") {
          setPoiContribution({
            step: "unsupported",
            placeId,
            lat,
            lng,
            selected: [],
            creating: false,
          });
        } else {
          setPoiMessage({ lat, lng, text: i18n.get("poiCouldNotAdd") });
        }
      })
      .finally(() => {
        resolvingPlaceIds.current.delete(placeId);
        setPoiResolving(false);
      });
  };

  const handleContributionShowOptions = () =>
    setPoiContribution((prev) => (prev ? { ...prev, step: "options" } : prev));

  const handleContributionBack = () =>
    setPoiContribution((prev) => (prev ? { ...prev, step: "intro" } : prev));

  const handleContributionToggleOption = (id: string) =>
    setPoiContribution((prev) =>
      prev
        ? {
            ...prev,
            selected: prev.selected.includes(id)
              ? prev.selected.filter((s) => s !== id)
              : [...prev.selected, id],
          }
        : prev,
    );

  // Commit: create the Grey Pin in our DB, then open its profile page.
  const handleContributionContinue = () => {
    if (!poiContribution || poiContribution.creating) return;
    const { placeId, venue } = poiContribution;

    // Existing grey venue: nothing to create — open its detail page so the user
    // can add photos / share experiences / subscribe.
    if (venue) {
      setPoiContribution(null);
      handleLoadVenue(venue);
      return;
    }

    setPoiContribution((prev) => (prev ? { ...prev, creating: true } : prev));

    createPlaceFromGoogle(placeId, Storage.getCurrentUserToken())
      .then((result) => {
        if (result.status === "success") {
          // A freshly created pin has no accessibility data yet, so it must
          // render grey (same rule showResolvedVenue applies).
          const hasAccessibilityData =
            (result.venue.reviewsDetailsSubmitted ?? 0) > 0;
          const resolvedVenue = {
            ...result.venue,
            venuePinType: hasAccessibilityData
              ? resolveVenuePinType(result.venue)
              : VenuePinType.Grey,
          };
          setResolvedPois((prev) =>
            prev.some((v) => v.placeId === resolvedVenue.placeId)
              ? prev
              : [...prev, resolvedVenue],
          );
          setPoiContribution(null);
          handleLoadVenue(resolvedVenue);
        } else {
          setPoiContribution((prev) => (prev ? { ...prev, creating: false } : prev));
          setPoiMessage({
            lat: poiContribution.lat,
            lng: poiContribution.lng,
            text: i18n.get("poiCouldNotAdd"),
          });
        }
      })
      .catch(() => {
        setPoiContribution((prev) => (prev ? { ...prev, creating: false } : prev));
      });
  };

  const handlePoiClick = (
    e: google.maps.MapMouseEvent & { placeId?: string; stop?: () => void },
  ): boolean => {
    if (!enableGooglePoiClick) return false;
    const placeId = e?.placeId;
    if (!placeId) return false;

    // Suppress Google's default POI info window.
    e.stop?.();

    handleCloseSnippet();
    handleClosePoi();

    resolvePlaceAndShow(placeId, {
      center: false,
      messageLat: e.latLng?.lat(),
      messageLng: e.latLng?.lng(),
    });

    return true;
  };

  // Determine where to show the snippet card relative to a pin.
  // Uses bounds-fraction interpolation to avoid large-number precision loss
  // that plagued the older `worldCoord * 2^zoom` approach.
  const getSnippetPosition = (location: Venue) => {
    const defaultWidth = 300;
    const defaultHeight = 400;
    const defaultPinHeight = 40;
    const fallback = {
      offset: { x: -(defaultWidth / 2), y: -defaultHeight - defaultPinHeight },
      className: "translate-x-[-50%] translate-y-[-105%]",
    };

    if (!mapRef) return fallback;

    try {
      const bounds = mapRef.getBounds();
      const projection = mapRef.getProjection();
      if (!bounds || !projection) return fallback;

      const mapDiv = mapRef.getDiv();
      const mapHeight = mapDiv.clientHeight;
      const mapWidth = mapDiv.clientWidth;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      // --- X: longitude is linear in Mercator ---
      let lngRange = ne.lng() - sw.lng();
      if (lngRange <= 0) lngRange += 360; // antimeridian wrap
      let pinLngOffset = location.longitude - sw.lng();
      if (pinLngOffset < 0) pinLngOffset += 360;
      const pinViewportX = (pinLngOffset / lngRange) * mapWidth;

      // --- Y: use world-coordinate fractions (stable, no large-number mult) ---
      const neWorld = projection.fromLatLngToPoint(ne);
      const swWorld = projection.fromLatLngToPoint(sw);
      const pinWorld = projection.fromLatLngToPoint(
        new google.maps.LatLng(location.latitude, location.longitude),
      );
      if (!neWorld || !swWorld || !pinWorld) return fallback;

      const worldHeightRange = swWorld.y - neWorld.y; // positive: south > north in world Y
      const pinViewportY =
        worldHeightRange > 0
          ? ((pinWorld.y - neWorld.y) / worldHeightRange) * mapHeight
          : mapHeight / 2;

      // Snippet actual dimensions
      const actualSnippetHeight = 400; // Actual height of the VenueCard snippet
      const actualSnippetWidth = 300; // Actual width of the VenueCard snippet

      // Snippet dimensions for space calculations (with buffer for shadows, borders, and safety)
      const snippetHeight = 440; // Height + extra buffer for shadows and top safety
      const snippetWidth = 320; // Width + extra buffer for shadows
      const pinHeight = 40; // Height of the pin marker
      const marginHorizontal = 50; // Increased safety margin from left/right edges
      const marginTop = 80; // Extra margin for top edge (more critical due to browser chrome)
      const marginBottom = 50; // Standard margin for bottom edge

      // Calculate available space on all sides
      const spaceAbove = pinViewportY - marginTop;
      const spaceBelow = mapHeight - pinViewportY - pinHeight - marginBottom;
      const spaceLeft = pinViewportX - marginHorizontal;
      const spaceRight = mapWidth - pinViewportX - marginHorizontal;

      // Determine if snippet fits in each direction with buffer
      // For top positioning, add extra 30px buffer to be more conservative
      const fitsAbove = spaceAbove >= snippetHeight + 30;
      const fitsBelow = spaceBelow >= snippetHeight;

      // For horizontal positioning, check if there's space for the full snippet width
      // when centered (need half width on each side) - with extra buffer
      const fitsCentered =
        spaceLeft >= snippetWidth / 2 + 10 &&
        spaceRight >= snippetWidth / 2 + 10;

      // CheckIcon if full snippet can fit to the right (when aligned left from pin) - with extra buffer
      const fitsFullyRight = spaceRight >= snippetWidth + 10;

      // CheckIcon if full snippet can fit to the left (when aligned right from pin) - with extra buffer
      const fitsFullyLeft = spaceLeft >= snippetWidth + 10;

      // Determine vertical position (above or below) - use actual dimensions for offset
      // Add extra offset padding for top positioning to prevent cutoff
      let yOffset = -actualSnippetHeight - pinHeight - 10; // Extra 10px padding when above
      let translateY = "translate-y-[-105%]";

      if (fitsAbove && !fitsBelow) {
        yOffset = -actualSnippetHeight - pinHeight - 10; // Extra padding
        translateY = "translate-y-[-105%]";
      } else if (fitsBelow && !fitsAbove) {
        yOffset = pinHeight + 5; // Small padding below too
        translateY = "translate-y-[5%]";
      } else if (!fitsAbove && !fitsBelow) {
        // Neither fits perfectly, strongly prefer below to avoid top cutoff
        if (spaceBelow > spaceAbove - 50) {
          // Bias towards below by 50px
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      } else {
        // Both fit, but prefer below if pin is in top 40% of screen to avoid top edge
        const pinInTopPortion = pinViewportY < mapHeight * 0.4;
        if (pinInTopPortion) {
          // Pin is near top, prefer showing below
          yOffset = pinHeight + 5;
          translateY = "translate-y-[5%]";
        } else {
          // Pin has plenty of space above, show above
          yOffset = -actualSnippetHeight - pinHeight - 10;
          translateY = "translate-y-[-105%]";
        }
      }

      // Determine horizontal position (center, left, or right aligned) - use actual dimensions for offset
      let xOffset = -(actualSnippetWidth / 2); // default: centered
      let translateX = "translate-x-[-50%]";

      if (fitsCentered) {
        // Snippet fits centered, keep default
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      } else if (!fitsFullyRight && fitsFullyLeft) {
        // Too close to right edge, align snippet to the LEFT of the pin
        xOffset = -actualSnippetWidth;
        translateX = "translate-x-[-100%]";
      } else if (!fitsFullyLeft && fitsFullyRight) {
        // Too close to left edge, align snippet to the RIGHT of the pin
        xOffset = 0;
        translateX = "translate-x-[0%]";
      } else if (!fitsFullyLeft && !fitsFullyRight) {
        // Doesn't fit on either side fully, choose side with more space
        if (spaceRight > spaceLeft) {
          xOffset = 0;
          translateX = "translate-x-[0%]";
        } else {
          xOffset = -actualSnippetWidth;
          translateX = "translate-x-[-100%]";
        }
      } else {
        // Both sides fit, prefer centered (already set as default)
        xOffset = -(actualSnippetWidth / 2);
        translateX = "translate-x-[-50%]";
      }

      return {
        offset: { x: xOffset, y: yOffset },
        className: `${translateX} ${translateY}`,
      };
    } catch (error) {
      console.error("Error calculating snippet position:", error);
      return fallback;
    }
  };

  const {
    location: userLocation,
    loading: locationLoading,
    error: locationError,
    requestUserLocation,
  } = useGeoLocation();

  // Result of search api
  // Note: We always call useHomeVenues() (React hooks rules), but when locationsOverride
  // is provided (e.g., from search page), we use that instead to avoid redundant data usage
  const {
    locations: locationsFromHook = [],
    isLoading,
    data: venuesData,
  } = useHomeVenues();

  // Use override locations if provided (search page passes filtered locations),
  // otherwise use locations from hook (saved places/venue details pages fetch their own data)
  const locations = locationsOverride || locationsFromHook;

  // Clear resolved POI pins whenever the underlying search results change
  // (new search / show nearby / re-search), so they don't linger across searches.
  useEffect(() => {
    setResolvedPois((prev) => (prev.length ? [] : prev));
    setSelectedPoiPosition(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Stable snippet venue + position — memoised on activeMarker so that unrelated
  // state changes (e.g. hoveredVenueId from VenueCard) do not produce new object
  // references, which would cause vis.gl to call infoWindow.setPosition() and
  // trigger a visible flicker on the Advanced Markers path.
  const snippetVenueAdv = useMemo(() => {
    if (!activeMarker) return null;
    return (
      locations.find((l) => l.id === activeMarker) ||
      resolvedPois.find((v) => v.id === activeMarker) ||
      null
    );
  }, [activeMarker, locations, resolvedPois]);

  const snippetPositionAdv = useMemo(
    () =>
      snippetVenueAdv
        ? { lat: snippetVenueAdv.latitude, lng: snippetVenueAdv.longitude }
        : null,
    [snippetVenueAdv],
  );

  // Pan the map to new search coordinates dispatched by performSearch.
  // This ensures the map follows an explicit location search even when
  // enableAutoZoom is disabled.
  useEffect(() => {
    const handlePanMapToCenter = (
      event: CustomEvent<{ lat: number; lng: number }>,
    ) => {
      const { lat, lng } = event.detail ?? {};
      if (lat == null || lng == null || !mapRef) return;
      mapRef.panTo({ lat, lng });
    };
    window.addEventListener(
      "panMapToCenter",
      handlePanMapToCenter as EventListener,
    );
    return () => {
      window.removeEventListener(
        "panMapToCenter",
        handlePanMapToCenter as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  // Resolve a Google place chosen from the search-bar auto-suggest: center the map,
  // drop a pin and show the preview (or an unsupported/error message).
  useEffect(() => {
    const handleSelectGooglePlace = (
      event: CustomEvent<{ placeId: string; sessionToken?: string }>,
    ) => {
      const placeId = event.detail?.placeId;
      if (!placeId || !enableGooglePoiClick) return;
      handleCloseSnippet();
      handleClosePoi();
      // Lookup-first: never silently create. In-DB → snippet, supported-but-new →
      // opt-in contribution card, unsupported type → terminal message card.
      handlePoiContribution(placeId, undefined, undefined, {
        center: true,
        sessionToken: event.detail?.sessionToken,
      });
    };
    window.addEventListener(
      "selectGooglePlace",
      handleSelectGooglePlace as EventListener,
    );
    return () => {
      window.removeEventListener(
        "selectGooglePlace",
        handleSelectGooglePlace as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, locations, resolvedPois]);

  // When arriving from the home page with a pending Google place (stored in
  // pendingGooglePlaceIdAtom), resolve it once the map is ready.
  useEffect(() => {
    if (!pendingGooglePlaceId || !mapRef || !enableGooglePoiClick) return;
    const placeId = pendingGooglePlaceId;
    setPendingGooglePlaceId(""); // clear immediately to prevent re-triggering
    window.dispatchEvent(
      new CustomEvent("selectGooglePlace", { detail: { placeId } }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGooglePlaceId, mapRef]);

  // Area / station / airport and compound selections no longer geocode here:
  // SearchBar's action dispatcher resolves coordinates server-side (closing the
  // session token) and writes them into searchPageParams, so the existing
  // search-params effect centres the map and runs the nearby search. This removed
  // the pendingGoogleAreaId / pendingCompoundSearch atoms and the
  // selectGoogleArea / compoundAreaSearch custom-event dance.

  const { setSearchPageParams, searchPageParams } = useSearchPageParams();
  const { mapMode, changeMapMode } = useMapMode();

  // Area / station / airport / compound selections arrive via searchPageParams
  // (SearchBar resolves the coords server-side). An already-loaded map doesn't
  // recenter from the `center` prop, so explicitly pan to the area when one of
  // these "search this area" selections is applied. Guarded by unixTimestamp so
  // we pan once per selection, not on unrelated re-renders.
  const lastPannedAreaTsRef = useRef<number | null>(null);
  useEffect(() => {
    if (!mapRef || searchPageParams.searchThisAreaClicked !== "yes") return;
    const ts = Number(searchPageParams.unixTimestamp) || 0;
    if (ts === lastPannedAreaTsRef.current) return;
    const lat = parseFloat(searchPageParams.latitude);
    const lng = parseFloat(searchPageParams.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    lastPannedAreaTsRef.current = ts;
    mapRef.panTo({ lat, lng });
    setMapCenter({ lat, lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapRef,
    searchPageParams.searchThisAreaClicked,
    searchPageParams.latitude,
    searchPageParams.longitude,
    searchPageParams.unixTimestamp,
  ]);

  // A deliberate keyword / compound / category search (performSearch) writes the
  // new search centre into searchPageParams and flags autoZoom="yes". An
  // already-mounted map ignores the `center` prop, so pan it to the new centre.
  // Progressive map-drag reloads and manual "search this area" set autoZoom="no",
  // so they never hit this. Deduped by coordinate value to avoid fighting drags.
  const lastPannedSearchCenterRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapRef || searchPageParams.autoZoom !== "yes") return;
    const lat = parseFloat(searchPageParams.latitude);
    const lng = parseFloat(searchPageParams.longitude);
    if (isNaN(lat) || isNaN(lng)) return;
    const key = `${lat},${lng}`;
    if (key === lastPannedSearchCenterRef.current) return;
    lastPannedSearchCenterRef.current = key;
    mapRef.panTo({ lat, lng });
    setMapCenter({ lat, lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapRef,
    searchPageParams.autoZoom,
    searchPageParams.latitude,
    searchPageParams.longitude,
  ]);

  useEffect(() => {
    // Skip this effect on the /search route
    if (isSearchRoute && !goToUserLocationRequestedRef.current) {
      return;
    }

    // console.log("goToUserLocationRequestedRef", goToUserLocationRequestedRef.current);

    if (userLocation && mapRef) {
      // UserIcon explicitly clicked "Go to current location" ï¿½ force full reset of search center
      if (goToUserLocationRequestedRef.current) {
        mapRef.panTo({
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        });
        setMapCenter({
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        });
        setSearchPageParams((prev) => ({
          ...prev,
          userlatitude: userLocation.latitude.toString(),
          userlongitude: userLocation.longitude.toString(),
          latitude: userLocation.latitude.toString(),
          longitude: userLocation.longitude.toString(),
          zoom: mapRef?.getZoom()?.toString() || "14",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        }));
        goToUserLocationRequestedRef.current = false;
        return;
      }

      setSearchPageParams((prev) => {
        // CheckIcon if latitude and longitude are already set (e.g., from shared search or custom location)
        const hasExistingCoordinates =
          prev.latitude !== null &&
          prev.longitude !== null &&
          prev.latitude !== undefined &&
          prev.longitude !== undefined;

        if (savedPlaceIdToView == "") {
          if (hasExistingCoordinates) {
            // Pan to the existing custom coordinates (shared from home page)
            const safeLat =
              parseFloat(prev.latitude || "") || defaultRegion.latitude;
            const safeLng =
              parseFloat(prev.longitude || "") || defaultRegion.longitude;
            mapRef.panTo({
              lat: safeLat,
              lng: safeLng,
            });
          } else {
            // No existing coordinates, use user's geolocation
            mapRef.panTo({
              lat: userLocation.latitude,
              lng: userLocation.longitude,
            });
          }
        }

        // If coordinates already exist AND this is the initial load, skip updating userlatitude/userlongitude
        // to prevent triggering an extra API call when navigating from home page with search params
        if (hasExistingCoordinates && !hasSetInitialUserLocationRef.current) {
          console.log(
            "[BasicMapView] Skipping initial userLocation update to prevent duplicate API call",
          );
          hasSetInitialUserLocationRef.current = true;
          return prev; // Return unchanged to avoid triggering API call
        }

        // If coordinates already exist, only update user location fields (not search center)
        if (hasExistingCoordinates) {
          return {
            ...prev,
            userlatitude: userLocation.latitude.toString(),
            userlongitude: userLocation.longitude.toString(),
            // DO NOT update latitude/longitude - preserve the custom location
            zoom: mapRef?.getZoom()?.toString() || "14",
            saved_place_id: savedPlaceIdToView,
            saved_place_user_id: savedPlaceByUserIdToView,
            saved_place_sortby: savedPlaceSortByToView,
            // Don't update unixTimestamp to avoid unnecessary API calls
          };
        }

        // No existing coordinates - set everything including search center
        hasSetInitialUserLocationRef.current = true;
        return {
          ...prev,
          userlatitude: userLocation.latitude.toString(),
          userlongitude: userLocation.longitude.toString(),
          latitude: userLocation.latitude.toString(),
          longitude: userLocation.longitude.toString(),
          zoom: mapRef?.getZoom()?.toString() || "14",
          saved_place_id: savedPlaceIdToView,
          saved_place_user_id: savedPlaceByUserIdToView,
          saved_place_sortby: savedPlaceSortByToView,
          unixTimestamp: Math.floor(Date.now() / 1000),
        };
      });
    }
  }, [
    userLocation,
    mapRef,
    setSearchPageParams,
    setMapCenter,
    savedPlaceIdToView,
    savedPlaceByUserIdToView,
    savedPlaceSortByToView,
  ]);

  useEffect(() => {
    // Skip this effect on the /search route
    if (isSearchRoute) {
      return;
    }

    if (locationError) {
      if (mapRef) {
        console.log(
          "Setting default location and loading pins savedPlaceIdToView",
          savedPlaceIdToView,
          "savedPlaceByUserIdToView",
          savedPlaceByUserIdToView,
        );
        setSearchPageParams((prev) => {
          // CheckIcon if latitude and longitude are already set (e.g., from shared search or custom location)
          const hasExistingCoordinates =
            prev.latitude !== null &&
            prev.longitude !== null &&
            prev.latitude !== undefined &&
            prev.longitude !== undefined;

          if (savedPlaceIdToView == "") {
            if (hasExistingCoordinates) {
              // Pan to the existing custom coordinates (shared from home page)
              const safeLat =
                parseFloat(prev.latitude || "") || defaultRegion.latitude;
              const safeLng =
                parseFloat(prev.longitude || "") || defaultRegion.longitude;
              mapRef.panTo({
                lat: safeLat,
                lng: safeLng,
              });
            } else {
              // No existing coordinates, use default region
              mapRef.panTo({
                lat: defaultRegion.latitude,
                lng: defaultRegion.longitude,
              });
            }
          }

          // If coordinates already exist AND this is the initial load, skip updating userlatitude/userlongitude
          if (hasExistingCoordinates && !hasSetInitialUserLocationRef.current) {
            console.log(
              "[BasicMapView] Skipping initial location error fallback update to prevent duplicate API call",
            );
            hasSetInitialUserLocationRef.current = true;
            return prev; // Return unchanged to avoid triggering API call
          }

          // If coordinates already exist, only update user location fields (not search center)
          if (hasExistingCoordinates) {
            return {
              ...prev,
              userlatitude: defaultRegion.latitude.toString(),
              userlongitude: defaultRegion.longitude.toString(),
              // DO NOT update latitude/longitude - preserve the custom location
              zoom: mapRef?.getZoom()?.toString() || "14",
              saved_place_id: savedPlaceIdToView,
              saved_place_user_id: savedPlaceByUserIdToView,
              saved_place_sortby: savedPlaceSortByToView,
              // Don't update unixTimestamp to avoid unnecessary API calls
            };
          }

          // No existing coordinates - set everything including search center
          hasSetInitialUserLocationRef.current = true;
          return {
            ...prev,
            userlatitude: defaultRegion.latitude.toString(),
            userlongitude: defaultRegion.longitude.toString(),
            latitude: defaultRegion.latitude.toString(),
            longitude: defaultRegion.longitude.toString(),
            zoom: mapRef?.getZoom()?.toString() || "14",
            saved_place_id: savedPlaceIdToView,
            saved_place_user_id: savedPlaceByUserIdToView,
            saved_place_sortby: savedPlaceSortByToView,
            unixTimestamp: Math.floor(Date.now() / 1000),
          };
        });
      }
    }
  }, [
    isSearchRoute,
    locationError,
    mapRef,
    savedPlaceByUserIdToView,
    savedPlaceIdToView,
    savedPlaceSortByToView,
    setSearchPageParams,
    setMapCenter,
  ]);

  const onMapMoved = () => {
    // Always publish the live viewport (centre + bounds) so the map-mode toggle
    // can search the current view without holding a map ref.
    const vpCenter = mapRef?.getCenter();
    const vpBounds = mapRef?.getBounds();
    if (vpCenter && vpBounds) {
      const ne = vpBounds.getNorthEast();
      const sw = vpBounds.getSouthWest();
      setMapViewport({
        lat: vpCenter.lat(),
        lng: vpCenter.lng(),
        ne: { lat: ne.lat(), lng: ne.lng() },
        sw: { lat: sw.lat(), lng: sw.lng() },
        zoom: mapRef?.getZoom() ?? 14,
      });
    }
    const vpZoom = mapRef?.getZoom();
    if (typeof vpZoom === "number") setMapZoom(vpZoom);

    // Skip if map hasn't completed initial load yet
    // This prevents overwriting synced location params when the idle event fires on initial map load
    if (!mapHasCompletedInitialLoadRef.current) {
      console.log("Skipping onMapMoved - map still initializing");
      // Mark as completed and store the current params
      // This ensures we only respond to REAL user movements, not initial map setup
      mapHasCompletedInitialLoadRef.current = true;
      lastInitializedParamsRef.current = {
        lat: searchPageParams.latitude,
        lng: searchPageParams.longitude,
      };
      // Seed zoom baseline so the first user zoom is compared against the real initial zoom
      lastReloadedZoomRef.current = mapRef?.getZoom() ?? null;
      console.log(
        "Map initialization complete - onMapMoved now enabled for params:",
        lastInitializedParamsRef.current,
      );

      // On the search page in Sociability ("Places with data") mode, send the
      // initial viewport bounds by default so the first load filters to the view.
      // Only when the POI-click feature (map-mode toggle) is enabled.
      if (
        isSearchRoute &&
        enableGooglePoiClick &&
        searchPageParams.map_mode !== "all" &&
        !searchPageParams.map_ne_lat
      ) {
        const initBounds = mapRef?.getBounds();
        const initNe = initBounds?.getNorthEast();
        const initSw = initBounds?.getSouthWest();
        if (initNe && initSw) {
          setSearchPageParams((prev) => ({
            ...prev,
            searchThisAreaClicked: "no",
            autoZoom: "no",
            map_ne_lat: initNe.lat().toString(),
            map_ne_lng: initNe.lng().toString(),
            map_sw_lat: initSw.lat().toString(),
            map_sw_lng: initSw.lng().toString(),
            unixTimestamp: Math.floor(Date.now() / 1000),
          }));
        }
      }
      return;
    }

    // Additional check: If searchPageParams have changed since we initialized,
    // skip this event (it's from the previous location's map settling)
    if (
      lastInitializedParamsRef.current.lat !== searchPageParams.latitude ||
      lastInitializedParamsRef.current.lng !== searchPageParams.longitude
    ) {
      console.log(
        "Skipping onMapMoved - searchPageParams changed since map initialization",
      );
      // Update to new params and wait for next idle
      lastInitializedParamsRef.current = {
        lat: searchPageParams.latitude,
        lng: searchPageParams.longitude,
      };
      return;
    }

    if (!mapSettings?.mapUpdate) {
      // Progressive search is disabled via mapSettings
      return;
    }

    if (savedPlaceIdToView !== "") {
      console.log("No progresive search on View saved places");
      return;
    }

    if (activeMarker) {
      // console.log("Marker active not loading new pins");
      return;
    }

    // Debounce: cancel any pending reload and wait for the user to settle
    // before firing — handles rapid zoom steps and mid-pan idle events
    const debounceMs = parseInt(mapSettings?.mapUpdateTimeout || "1000", 10);
    if (progressiveSearchTimerRef.current) {
      clearTimeout(progressiveSearchTimerRef.current);
    }

    progressiveSearchTimerRef.current = setTimeout(() => {
      progressiveSearchTimerRef.current = null;

      const newCenter = mapRef?.getCenter();
      const currentZoom = mapRef?.getZoom() ?? null;

      const prevCenter = new google.maps.LatLng(
        parseFloat(searchPageParams.latitude),
        parseFloat(searchPageParams.longitude),
      );

      const distanceInMeters =
        google.maps.geometry.spherical.computeDistanceBetween(
          prevCenter,
          newCenter,
        );

      // Scale the distance threshold by zoom level — the base distance (from settings)
      // applies at zoom 14. Each zoom level in halves the threshold (map viewport shrinks),
      // each level out doubles it. Clamped between 50m and 5000m.
      const BASE_ZOOM = 14;
      const baseDistanceMeters = parseFloat(mapSettings?.mapUpdateDistance || "0.5") * 1000;
      const zoomForThreshold = currentZoom ?? BASE_ZOOM;
      const dynamicThresholdMeters = Math.min(
        5000,
        Math.max(50, baseDistanceMeters * Math.pow(2, BASE_ZOOM - zoomForThreshold)),
      );

      const movedBeyondThreshold = distanceInMeters > dynamicThresholdMeters;

      // Require at least 1 zoom level of change since the last reload
      // (debounce above already handles rapid stepping — this just prevents
      //  spurious re-fires when idle fires without a real zoom change)
      const ZOOM_LEVEL_THRESHOLD = 1;
      const zoomDelta =
        lastReloadedZoomRef.current !== null && currentZoom !== null
          ? Math.abs(currentZoom - lastReloadedZoomRef.current)
          : 0;
      const zoomChanged =
        mapSettings?.mapUpdateWithZoom && zoomDelta >= ZOOM_LEVEL_THRESHOLD;

      if (movedBeyondThreshold || zoomChanged) {
        console.log(
          zoomChanged
            ? `Progressive search: reloading on zoom change (delta ${zoomDelta}, ${lastReloadedZoomRef.current} -> ${currentZoom})`
            : `Progressive search: reloading on map move (${distanceInMeters.toFixed(0)}m, threshold ${dynamicThresholdMeters.toFixed(0)}m at zoom ${zoomForThreshold})`,
          newCenter?.lat().toString(),
          newCenter?.lng().toString(),
        );
        // Update last-reloaded zoom so future deltas are measured from here
        lastReloadedZoomRef.current = currentZoom;
        // Mark that the user moved the map to suppress auto-zoom briefly
        userMovedMapAtRef.current = Date.now();

        // Capture the visible viewport bounds so the backend can filter
        // to exactly what the user can see (replaces radius circle at zoom >= 14)
        const bounds = mapRef?.getBounds();
        const ne = bounds?.getNorthEast();
        const sw = bounds?.getSouthWest();

        setSearchPageParams((prev) => {
          const params = {
            ...prev,
            latitude: newCenter?.lat().toString() || "",
            longitude: newCenter?.lng().toString() || "",
            zoom: currentZoom?.toString() || "14",
            searchThisAreaClicked: "no",
            autoZoom: "no", // progressive reload must never trigger auto-zoom
            unixTimestamp: Math.floor(Date.now() / 1000),
            includeGoogleFallback: "false",
            ...(ne && sw
              ? {
                  map_ne_lat: ne.lat().toString(),
                  map_ne_lng: ne.lng().toString(),
                  map_sw_lat: sw.lat().toString(),
                  map_sw_lng: sw.lng().toString(),
                }
              : {
                  map_ne_lat: undefined,
                  map_ne_lng: undefined,
                  map_sw_lat: undefined,
                  map_sw_lng: undefined,
                }),
          };
          // Clear expand_radius specifically — other search types (brand, venue_name, etc.) should persist
          if (prev.search_type === "expand_radius") {
            delete params.search_type;
            delete params.search_value;
          }
          return params;
        });
      }
    }, debounceMs);
  };

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    setMapRef(map);
  }, []);

  const onUnmount = React.useCallback(function callback() {
    if (progressiveSearchTimerRef.current) {
      clearTimeout(progressiveSearchTimerRef.current);
    }
    setMapRef(null);
  }, []);

  const getPinIcon = (venue: Venue) => {
    // console.log("venue.venuePinType isMapperViewActive ", isMapperViewActive, venue.venuePinType);
    // UserIcon view: Only show Orange/Grey pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPin;
      } else {
        return OrangePin;
      }
    }

    // Mapper view: Show all 6 pin types
    switch (venue.venuePinType) {
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      case VenuePinType.Orange:
        return OrangePin;
      case VenuePinType.Grey:
        return GreyPin;
      default:
        return GreyPin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // UserIcon view: Only show Orange/Grey selected pins
    // if (!isMapperViewActive) {
    if (venue.venuePinType === VenuePinType.Grey) {
      return GreyPinSelected;
    } else {
      return OrangePinSelected;
    }
    // }

    // // Mapper view: Show all pin types (selected variants only for Grey/Orange)
    // switch (venue.venuePinType) {
    //   case VenuePinType.Grey:
    //     return GreyPinSelected;
    //   case VenuePinType.Orange:
    //     return OrangePinSelected;
    //   case VenuePinType.Green:
    //     return GreenPin;
    //   case VenuePinType.Black:
    //     return BlackPin;
    //   case VenuePinType.Blue:
    //     return BluePin;
    //   case VenuePinType.Purple:
    //     return PurplePin;
    //   default:
    //     return GreyPinSelected;
    // }
  };

  const radius = 75; // default radius in km
  const unit = "km"; // or'miles'
  const toRadians = (degrees) => {
    return degrees * (Math.PI / 180);
  };

  // Ref to track which venuesData we already auto-zoomed for, so we don't
  // re-run auto-zoom (and trigger another API call) for the same result set
  const lastAutoZoomedDataRef = useRef<any>(null);

  // Effect to auto-update zoom level based on search_distance_used (gated by flag)
  useEffect(() => {
    console.log("search_distance_used", venuesData?.search_distance_used);
    console.log(
      "search_original_distance",
      venuesData?.search_original_distance,
    );

    // Only auto-zoom when explicitly flagged by search click
    if (mapRef && enableAutoZoom && searchPageParams.autoZoom === "yes") {
      // Skip if we already auto-zoomed for this exact data set
      if (venuesData === lastAutoZoomedDataRef.current) {
        return;
      }
      lastAutoZoomedDataRef.current = venuesData;

      // Case 1: API returned different distances - adjust zoom based on search_distance_used
      if (
        venuesData?.search_distance_used &&
        venuesData.search_distance_used !== venuesData.search_original_distance
      ) {
        console.log(
          "Received modified search distance:",
          venuesData.search_distance_used,
        );

        const distanceInKm = venuesData.search_distance_used;

        // Calculate the appropriate zoom level
        const newZoomLevel = calculateZoomFromDistance(distanceInKm);
        console.log(
          "Calculated zoom level:",
          newZoomLevel,
          "from distance:",
          distanceInKm,
          "km",
        );

        // Only update if we need to zoom OUT (expand view)
        // Lower zoom number = zoomed out (wider area), higher = zoomed in (closer)
        // Only allow zooming out, never zoom in
        const currentZoom = mapRef.getZoom();
        if (newZoomLevel < currentZoom) {
          console.log("Zooming OUT from", currentZoom, "to", newZoomLevel);

          // Only update the map visually ï¿½ do NOT call setSearchPageParams
          // to avoid triggering another API call
          mapRef.setZoom(newZoomLevel);
        } else {
          console.log(
            "Skipping zoom IN - current zoom",
            currentZoom,
            "is already closer than",
            newZoomLevel,
          );
        }
      }
      // Case 2: No distance information - use fitBounds to show all venues (only zoom OUT, never IN)
      else if (
        venuesData &&
        locations.length > 0 &&
        !venuesData.search_distance_used &&
        !venuesData.search_original_distance
      ) {
        console.log(
          "No distance information received, checking if fitBounds should apply",
        );

        // Zoom constraints (same as home page)
        const MIN_ZOOM = 11; // Don't zoom out beyond this
        const MAX_ZOOM = 16; // Don't zoom in beyond this

        try {
          const currentZoom = mapRef.getZoom();
          console.log("Current zoom level:", currentZoom);

          // Create bounds from all venue locations
          const bounds = new google.maps.LatLngBounds();
          locations.forEach((venue) => {
            if (venue.latitude && venue.longitude) {
              bounds.extend(
                new google.maps.LatLng(venue.latitude, venue.longitude),
              );
            }
          });

          if (bounds.isEmpty()) {
            console.log("Bounds are empty, skipping fitBounds");
            return;
          }

          // Calculate the distance/spread of venues to estimate zoom level
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();

          // Calculate approximate distance in km using Haversine formula
          const latDiff = ne.lat() - sw.lat();
          const lngDiff = ne.lng() - sw.lng();
          const avgLat = (ne.lat() + sw.lat()) / 2;

          // Rough distance calculation (good enough for zoom estimation)
          const latDistance = latDiff * 111; // 1 degree lat ï¿½ 111 km
          const lngDistance =
            lngDiff * 111 * Math.cos((avgLat * Math.PI) / 180);
          const maxDistance = Math.max(latDistance, lngDistance);

          // Calculate what zoom level would be appropriate for this distance
          const estimatedZoom = calculateZoomFromDistance(maxDistance * 1.2); // Add 20% margin
          console.log(
            "Estimated zoom for bounds:",
            estimatedZoom,
            "from distance:",
            maxDistance.toFixed(2),
            "km",
          );

          // Only apply fitBounds if it would zoom OUT (lower number = more zoomed out)
          // or if this is the initial load (currentZoom is undefined)
          if (currentZoom === undefined || estimatedZoom < currentZoom) {
            console.log(
              "Applying fitBounds - zooming OUT from",
              currentZoom,
              "to approximately",
              estimatedZoom,
            );

            // Apply fitBounds with padding
            mapRef.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50,
            });

            // Enforce min/max zoom constraints after fitBounds
            setTimeout(() => {
              const actualZoom = mapRef.getZoom();
              if (actualZoom !== undefined) {
                if (actualZoom < MIN_ZOOM) {
                  mapRef.setZoom(MIN_ZOOM);
                  console.log("Zoom limited to minimum:", MIN_ZOOM);
                } else if (actualZoom > MAX_ZOOM) {
                  mapRef.setZoom(MAX_ZOOM);
                  console.log("Zoom limited to maximum:", MAX_ZOOM);
                } else {
                  console.log("Dynamic zoom applied:", actualZoom);
                }
              }
            }, 50); // Minimal delay to allow fitBounds to complete
          } else {
            console.log(
              "Respecting manual zoom - staying at",
              currentZoom,
              "(fitBounds would zoom IN to approximately",
              estimatedZoom,
              ")",
            );
          }
        } catch (error) {
          console.error("Error applying dynamic fitBounds zoom:", error);
        }
      }
    }
  }, [venuesData, locations.length, mapRef, searchPageParams.autoZoom]);

  // Fit the map to the result pins after a deliberate area/category search
  // (e.g. "cafe in pinner"). Driven by fitMapToResultsAtom rather than the
  // autoZoom flag, so it works even when VITE_ENABLE_AUTO_ZOOM is disabled.
  // Waits for fresh data (different ref than at request time) so it never frames
  // the previous search's pins, and clamps to sane min/max zoom.
  const pendingFitRef = useRef(false);
  const lastFitRequestRef = useRef(0);
  const fitBaselineDataRef = useRef<unknown>(null);
  useEffect(() => {
    if (fitMapToResults !== 0 && fitMapToResults !== lastFitRequestRef.current) {
      lastFitRequestRef.current = fitMapToResults;
      pendingFitRef.current = true;
      fitBaselineDataRef.current = venuesData;
    }
    if (!pendingFitRef.current || !mapRef) return;
    // Hold until the new search's data has actually loaded.
    if (venuesData === fitBaselineDataRef.current || locations.length === 0) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    locations.forEach((v) => {
      if (v.latitude && v.longitude) {
        bounds.extend(new google.maps.LatLng(v.latitude, v.longitude));
      }
    });
    if (bounds.isEmpty()) return;

    pendingFitRef.current = false;
    mapRef.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });

    const MIN_ZOOM = 11;
    const MAX_ZOOM = 16;
    window.setTimeout(() => {
      const z = mapRef.getZoom();
      if (z === undefined) return;
      if (z < MIN_ZOOM) mapRef.setZoom(MIN_ZOOM);
      else if (z > MAX_ZOOM) mapRef.setZoom(MAX_ZOOM);
    }, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitMapToResults, venuesData, locations.length, mapRef]);

  // Effect to pan the map to the first result for expand_radius searches
  // This is separate from auto-zoom ï¿½ it only handles map centering
  useEffect(() => {
    if (
      mapRef &&
      searchPageParams.search_type === "expand_radius" &&
      locations.length > 0
    ) {
      const firstResult = locations[0];
      if (firstResult?.latitude && firstResult?.longitude) {
        const resultCenter = {
          lat: firstResult.latitude,
          lng: firstResult.longitude,
        };
        console.log("expand_radius: Panning map to first result", resultCenter);
        mapRef.panTo(resultCenter);
        setMapCenter(resultCenter);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, mapRef, searchPageParams.search_type]);

  // Reset map initialization flag when location changes significantly
  // This allows onMapMoved to be skipped during the next map load
  useEffect(() => {
    // Reset the flag when searchPageParams location changes
    // This ensures we skip onMapMoved during the new map initialization
    mapHasCompletedInitialLoadRef.current = false;
    lastInitializedParamsRef.current = { lat: null, lng: null };
    console.log("Location changed - resetting map initialization flags");
  }, [searchPageParams.latitude, searchPageParams.longitude]);

  useEffect(() => {
    if (
      locations.length &&
      mapRef &&
      userLocation &&
      savedPlaceIdToView !== ""
    ) {
      const calculateDistance = (lat1, lon1, lat2, lon2, unit) => {
        const R = unit === "miles" ? 3958.8 : 6371; // Radius of the Earth in miles or km
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);
        const a =
          Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        return distance;
      };

      const filterLocationsByRadius = (locations, center, radius, unit) => {
        return locations.filter((location) => {
          const distance = calculateDistance(
            center.latitude,
            center.longitude,
            location.latitude,
            location.longitude,
            unit,
          );
          return distance <= radius;
        });
      };

      const filteredLocations = filterLocationsByRadius(
        locations,
        userLocation,
        radius,
        unit,
      );
      const bounds = new google.maps.LatLngBounds();
      filteredLocations.forEach((location) => {
        bounds.extend(
          new google.maps.LatLng(location.latitude, location.longitude),
        );
      });

      bounds.extend(
        new google.maps.LatLng(userLocation.latitude, userLocation.longitude),
      );

      mapRef.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, userLocation, radius, unit, mapRef]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  // Two map modes (search route only): "all" shows native Google business POIs
  // (clickable -> grey pin) and hides our pins; "sociability" (default) hides all
  // POIs for a clean map and plots our mapped venues. Off the search route, keep
  // the previous behaviour (business POIs shown + clickable when the feature is on).
  const isAllMode = isSearchRoute && searchPageParams.map_mode === "all";
  // "declutter": same mapped venues as sociability, but drawn as individual pins
  // that thin out by zoom via Google's collision engine (no clustering).
  const isDeclutterMode =
    isSearchRoute && searchPageParams.map_mode === "declutter";
  const showNativePois = isSearchRoute ? isAllMode : enableGooglePoiClick;
  const poiClickable = enableGooglePoiClick && showNativePois;
  // Vector maps ignore JS `styles`, so POI visibility is controlled by the Map
  // ID's cloud style. The default googleMapId (clean, POIs hidden) is used
  // everywhere; only the "All places" tab swaps to a Map ID whose style shows
  // POIs so they're visible + clickable. Falls back to googleMapId when unset.
  const activeMapId =
    isAllMode && googleMapIdPoi ? googleMapIdPoi : googleMapId;
  const baseStyles = isDark ? darkModeStyles : lightModeStyles;
  const poiStyles = enableGooglePoiClick
    ? [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        ...(showNativePois
          ? [{ featureType: "poi.business", stylers: [{ visibility: "on" }] }]
          : []),
      ]
    : [];
  const mapOptions = {
    keyboardShortcuts: false,
    fullscreenControl: false,
    disableDefaultUI: true,
    clickableIcons: poiClickable,
    styles: [...((baseStyles as any[]) || []), ...poiStyles],
  };

  // ---- Marker clustering (search-route Sociability mode only) ----
  const shouldCluster =
    isSearchRoute && !isAllMode && !isDeclutterMode && enableAdvancedMarkers;
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const [clusterMarkers, setClusterMarkers] = useState<
    Record<string, google.maps.marker.AdvancedMarkerElement>
  >({});
  // Current zoom — drives when always-on name labels appear (clusters dissolved).
  const [mapZoom, setMapZoom] = useState<number>(16);
  // Separate collision name markers only in clustered "Places with data" mode
  // (pins there don't collide, so names need independent decluttering).
  const showNameLabels = shouldCluster && mapZoom >= LABEL_MIN_ZOOM;
  // Explore / All places draw names inline on the pin, revealed at higher zoom
  // so lower zooms stay uncluttered and Google's collision thins pins+names.
  const showInlineNames =
    (isDeclutterMode || isAllMode) && mapZoom >= UNCLUSTERED_LABEL_MIN_ZOOM;

  // Register/unregister a pin's marker element with the clusterer.
  const setClusterMarkerRef = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => {
      setClusterMarkers((prev) => {
        if ((marker && prev[key]) || (!marker && !prev[key])) return prev;
        if (marker) return { ...prev, [key]: marker };
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [],
  );

  // Stable per-key ref callbacks so steady-state renders (hover/snippet) don't
  // re-register markers and thrash the clusterer.
  const markerRefCbs = useRef<
    Map<string, (m: google.maps.marker.AdvancedMarkerElement | null) => void>
  >(new Map());
  const getClusterMarkerRef = useCallback(
    (key: string) => {
      let cb = markerRefCbs.current.get(key);
      if (!cb) {
        cb = (m: google.maps.marker.AdvancedMarkerElement | null) =>
          setClusterMarkerRef(m, key);
        markerRefCbs.current.set(key, cb);
      }
      return cb;
    },
    [setClusterMarkerRef],
  );

  // Create / tear down the clusterer. Always rebuilt on `mapRef` change:
  // swapping activeMapId (e.g. "Places with data" <-> "All places") forces
  // vis.gl to recreate the underlying google.maps.Map, so a clusterer bound
  // to the old instance would otherwise keep its markers silently detached.
  useEffect(() => {
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }
    if (!mapRef || !shouldCluster) return;
    clustererRef.current = new MarkerClusterer({
      map: mapRef,
      renderer: orangeClusterRenderer,
      algorithm: new SuperClusterAlgorithm({
        radius: CLUSTER_RADIUS,
        maxZoom: CLUSTER_MAX_ZOOM,
      }),
    });
    // Re-attach markers already registered this render (the sync effect
    // below only fires on `clusterMarkers` changes).
    clustererRef.current.addMarkers(Object.values(clusterMarkers));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clusterMarkers read intentionally, not tracked
  }, [mapRef, shouldCluster]);

  // Keep the clusterer's markers in sync with the rendered venue pins.
  useEffect(() => {
    const clusterer = clustererRef.current;
    if (!clusterer) return;
    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(clusterMarkers));
  }, [clusterMarkers]);

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block",
      )}
    >
      {isMapsLoaded && (
        enableAdvancedMarkers ? (
          /* ---- vis.gl Advanced Markers path ---- */
          <APIProvider apiKey={googleMapKey} libraries={["marker", "geometry"]} version="3.55">
            <VisGlMap
              mapId={activeMapId}
              colorScheme={isDark ? "DARK" : "LIGHT"}
              style={{ width: "100%", height: "100%" }}
              defaultCenter={mapCenter}
              defaultZoom={16}
              gestureHandling="greedy"
              disableDefaultUI
              keyboardShortcuts={false}
              clickableIcons={poiClickable}
              onClick={(e: VisGlMapMouseEvent) => {
                if (poiClickable && e.detail.placeId) {
                  e.stop();
                  handleCloseSnippet();
                  handleClosePoi();
                  const placeId = e.detail.placeId;
                  // A POI we already show as a mapped (orange) pin needs no
                  // highlight ring — the orange pin itself signals selection.
                  const matchedMapped = [...locations, ...resolvedPois].some(
                    (v) =>
                      v.placeId === placeId &&
                      v.venuePinType !== VenuePinType.Grey,
                  );
                  if (
                    !matchedMapped &&
                    e.detail.latLng?.lat != null &&
                    e.detail.latLng?.lng != null
                  ) {
                    setSelectedPoiPosition({
                      lat: e.detail.latLng.lat,
                      lng: e.detail.latLng.lng,
                    });
                  }
                  if (isSearchRoute && isAllMode) {
                    handlePoiContribution(
                      placeId,
                      e.detail.latLng?.lat ?? undefined,
                      e.detail.latLng?.lng ?? undefined,
                    );
                  } else {
                    resolvePlaceAndShow(placeId, {
                      center: false,
                      messageLat: e.detail.latLng?.lat ?? undefined,
                      messageLng: e.detail.latLng?.lng ?? undefined,
                    });
                  }
                  return;
                }
                setActiveMarker(null);
                setSnippetPosition(null);
                setTooltipVenue(null);
                setHoveredMarkerId(null);
                setHoveredVenueId("");
                setSnippetOpen(false);
                setSelectedPoiPosition(null);
                handleClosePoi();
              }}
              onIdle={() => onMapMoved()}
              onZoomChanged={() => {
                const z = mapRef?.getZoom();
                if (typeof z === "number") setMapZoom(z);
                setActiveMarker(null);
                setSnippetPosition(null);
                setTooltipVenue(null);
                setHoveredMarkerId(null);
                setSnippetOpen(false);
                setSelectedPoiPosition(null);
              }}
              onDragstart={() => {
                setActiveMarker(null);
                setSnippetPosition(null);
                setTooltipVenue(null);
                setHoveredMarkerId(null);
                setSnippetOpen(false);
                setSelectedPoiPosition(null);
              }}
            >
              {/* Sync vis.gl map instance into the shared mapRef */}
              <MapRefCapture onMap={setMapRef} />

              {/* Venue markers. In "all" mode our mapped (orange) pins render on
                  top of the native Google POIs so users can spot venues with
                  data; grey (no-data) venues are hidden since the Google POI
                  already represents them. */}
              {locations.map((location, index) => {
                if (
                  showNativePois &&
                  location.venuePinType === VenuePinType.Grey
                ) {
                  return null;
                }
                const isSelected = !!(
                  selectedVenue && selectedVenue.id === location.id
                );
                const isHovered =
                  (hoveredMarkerId === location.id ||
                    hoveredVenueId === location.id) &&
                  !snippetOpen;

                // Shared handler used by both the outer AdvancedMarker (gmp-click /
                // gmpClickable keyboard path) and the inner AdvancedPinContent div
                // (tabIndex keyboard path).  The content div calls stopPropagation so
                // mouse clicks only fire once.
                const handleVenueMarkerClick = () => {
                  // place_pin_selected — user tapped a search-result map pin.
                  // pin_type via the shared canonical helper (controlled vocab);
                  // entry_index = the pin's position in the results list. Shared map
                  // widget across multiple page contexts → fire the primitive.
                  trackEvent("place_pin_selected", {
                    brand_id: location.brandId ?? "",
                    brand_name: location.brandName ?? "",
                    business_id: location.business?.id?.toString() ?? "",
                    entry_index: index,
                    pin_type: pinTypeForVenue(location),
                    place_name: location?.name ?? "",
                  });

                  if (
                    currentVenueTypePage === 1 ||
                    props.mapSource === "venuedetails"
                  ) {
                    handleLoadVenue(location);
                  } else {
                    handleShowSnippet(location.id);
                  }
                };

                // Collision priority via zIndex: selected/hovered on top, then
                // orange ("has data") above grey, then nearer (earlier) above later.
                // In declutter/all-places mode priority is the review count so the
                // most-reviewed venues win Google's collision (like POI prominence),
                // and pins decluster by zoom instead of always-on/off.
                const isGreyPin = location.venuePinType === VenuePinType.Grey;
                const reviewScore =
                  location.reviewsSubmitted ||
                  location.reviewsDetailsSubmitted ||
                  0;
                const collisionPriority =
                  isDeclutterMode || isAllMode
                    ? reviewScore
                    : (isGreyPin ? 0 : 100000) + (locations.length - index);
                const markerZIndex = isSelected
                  ? 1000000
                  : isHovered
                    ? 999999
                    : collisionPriority;

                return (
                  <AdvancedMarker
                    key={`adv-marker-${location.id}`}
                    ref={
                      shouldCluster
                        ? getClusterMarkerRef(`adv-marker-${location.id}`)
                        : undefined
                    }
                    position={{ lat: location.latitude, lng: location.longitude }}
                    zIndex={markerZIndex}
                    collisionBehavior={
                      isDeclutterMode || isAllMode
                        ? google.maps.CollisionBehavior
                            .OPTIONAL_AND_HIDES_LOWER_PRIORITY
                        : undefined
                    }
                    onClick={handleVenueMarkerClick}
                  >
                    <AdvancedPinContent
                      venue={location}
                      isSelected={isSelected}
                      isHovered={isHovered}
                      isMapperView={isMapperViewActive}
                      showLabel={showInlineNames}
                      suppressLabel={shouldCluster && !isMapperViewActive}
                      onMouseEnter={() => {
                        if (snippetOpen) return;
                        setTooltipVenue(location);
                        setHoveredVenueId(location.id);
                        setHoveredMarkerId(location.id);
                      }}
                      onMouseLeave={() => {
                        if (snippetOpen) return;
                        setTooltipVenue(null);
                        setHoveredVenueId("");
                        setHoveredMarkerId(null);
                      }}
                    />
                  </AdvancedMarker>
                );
              })}

              {/* Name labels as separate OPTIONAL markers, so collision can
                  declutter names independently of pins. Post-cluster only. */}
              {showNameLabels &&
                locations.map((location, index) => {
                  if (!location.name) return null;
                  const isSelected = !!(
                    selectedVenue && selectedVenue.id === location.id
                  );
                  const isGreyPin =
                    location.venuePinType === VenuePinType.Grey;
                  // Same priority ranking as the pins; selected always wins.
                  const labelZIndex = isSelected
                    ? 1000000
                    : (isGreyPin ? 0 : 100000) + (locations.length - index);
                  const category =
                    location.calcVenueType || location.businessType || "";
                  // AdvancedMarkers anchor bottom-center by default, so a
                  // same-size invisible spacer reproduces the pin's anchor
                  // frame, letting the label position off it like the inline
                  // label in AdvancedPinContent.
                  const pinSize = isSelected ? PIN_SIZE_SELECTED : PIN_SIZE_DEFAULT;
                  const pinHeight = pinSize * PIN_ASPECT;
                  const pinCentreTop = getPinCentreTop(pinSize);
                  return (
                    <AdvancedMarker
                      key={`adv-label-${location.id}`}
                      position={{
                        lat: location.latitude,
                        lng: location.longitude,
                      }}
                      zIndex={labelZIndex}
                      collisionBehavior={
                        google.maps.CollisionBehavior
                          .OPTIONAL_AND_HIDES_LOWER_PRIORITY
                      }
                    >
                      <div
                        className="relative"
                        style={{
                          width: pinSize,
                          height: pinHeight,
                          pointerEvents: "none",
                        }}
                      >
                        {/* Name + category, right of the pin (Google POI style). */}
                        <div
                          className="absolute left-full ml-1.5 flex flex-col select-none"
                          style={{
                            top: pinCentreTop,
                            transform: "translateY(-55%)",
                          }}
                        >
                          <span
                            className="text-[13px] font-medium leading-tight whitespace-nowrap max-w-[160px] truncate"
                            style={{ textShadow: NAME_HALO, color: NAME_TEXT_COLOR }}
                          >
                            {location.name}
                          </span>
                          {category && (
                            <span
                              className="text-[11px] leading-tight whitespace-nowrap max-w-[160px] truncate"
                              style={{
                                textShadow: NAME_HALO,
                                color: CATEGORY_TEXT_COLOR,
                              }}
                            >
                              {category}
                            </span>
                          )}
                        </div>
                      </div>
                    </AdvancedMarker>
                  );
                })}

              {/* Selected-state highlight ring on the clicked native Google POI. */}
              {poiClickable && selectedPoiPosition && (
                <AdvancedMarker
                  key="poi-highlight-ring"
                  position={selectedPoiPosition}
                  zIndex={500}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "9999px",
                      pointerEvents: "none",
                      transform: "translateY(5%)",
                      background:
                        "radial-gradient(circle, rgba(26,115,232,0.16) 0%, rgba(26,115,232,0.16) 55%, rgba(26,115,232,0) 76%)",
                      boxShadow: "0 0 0 2px rgba(26,115,232,0.35)",
                    }}
                  />
                </AdvancedMarker>
              )}

              {/* POI-resolved venue markers */}
              {resolvedPois
                .filter(
                  (v) =>
                    !locations.some(
                      (l) =>
                        (v.placeId && l.placeId === v.placeId) || l.id === v.id,
                    ),
                )
                // Grey (no accessibility data) venues duplicate the native Google
                // POI icon/label whenever POIs are visible, without adding any
                // value to the user — skip rendering our own pin for them.
                .filter(
                  (v) =>
                    !(showNativePois && v.venuePinType === VenuePinType.Grey),
                )
                .map((poiVenue) => {
                  const handlePoiMarkerClick = () => {
                    if (
                      currentVenueTypePage === 1 ||
                      props.mapSource === "venuedetails"
                    ) {
                      handleLoadVenue(poiVenue);
                    } else {
                      setActiveMarker(poiVenue.id);
                      setSnippetPosition(getSnippetPosition(poiVenue));
                      setSnippetOpen(true);
                    }
                  };
                  return (
                    <AdvancedMarker
                      key={`adv-poi-${poiVenue.id}`}
                      position={{
                        lat: poiVenue.latitude,
                        lng: poiVenue.longitude,
                      }}
                      zIndex={1001}
                      collisionBehavior={
                        isAllMode
                          ? google.maps.CollisionBehavior
                              .REQUIRED_AND_HIDES_OPTIONAL
                          : undefined
                      }
                      onClick={handlePoiMarkerClick}
                    >
                      <AdvancedPinContent
                        venue={poiVenue}
                        isSelected={activeMarker === poiVenue.id}
                        isHovered={false}
                        isMapperView={isMapperViewActive}
                      />
                    </AdvancedMarker>
                  );
                })}

              {/* User location marker */}
              {userLocation && (
                <AdvancedMarker
                  key="adv-user-location"
                  position={{
                    lat: userLocation.latitude,
                    lng: userLocation.longitude,
                  }}
                  zIndex={10000}
                  title={i18n.get("yourLocation") || "Your current location"}
                >
                  <img src={YouAre} width={30} height={30} alt="" draggable={false} />
                </AdvancedMarker>
              )}

              {/* Desktop snippet InfoWindow — replaces OverlayView nested in legacy Marker */}
              {currentVenueTypePage === 0 &&
                !selectedVenue &&
                snippetVenueAdv &&
                snippetPositionAdv && (
                    <InfoWindow
                      position={snippetPositionAdv}
                      headerDisabled
                      pixelOffset={SNIPPET_PIXEL_OFFSET}
                      className="adv-map-snippet"
                    >
                      <div className="hidden lg:block w-[300px]">
                        <div className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full">
                          <style>{`
                            .map-snippet-wrapper button.bg-opacity-30 {
                            right: 52px !important;
                            }`}</style>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseSnippet();
                            }}
                            className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors"
                            aria-label="Close venue preview"
                            type="button"
                          >
                            <XIcon
                              size={16}
                              weight="bold"
                              className="text-fg-neutral-strong"
                            />
                          </button>
                          <button
                            type="button"
                            className="w-full text-left rounded p-0 focus-blue cursor-pointer"
                            aria-label={`View details for ${snippetVenueAdv.name}`}
                          >
                            <VenueCard
                              location={snippetVenueAdv}
                              onOpen={() => handleLoadVenue(snippetVenueAdv)}
                              onSaveVenue={handleSaveVenue}
                              savedPlaces={savedPlaces}
                              truncateText={true}
                              index={0}
                              totalCount={locations.length}
                              showMetaInfo={true}
                              showOpeningHours={true}
                              showVariantIndicators={true}
                              showDescription={true}
                              showAddress={false}
                              inMapView={true}
                            />
                          </button>
                        </div>
                      </div>
                    </InfoWindow>
                )}

              {/* Unsupported / error message at the clicked POI point */}
              {poiMessage && (
                <InfoWindow
                  position={{ lat: poiMessage.lat, lng: poiMessage.lng }}
                  onClose={() => setPoiMessage(null)}
                  onCloseClick={() => setPoiMessage(null)}
                  headerDisabled
                  pixelOffset={[0, -20]}
                  className="adv-map-snippet"
                >
                  <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-3 rounded-lg w-max max-w-[260px] relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPoiMessage(null);
                      }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
                      aria-label="Close"
                      type="button"
                    >
                      <XIcon size={14} weight="bold" />
                    </button>
                    <div className="body-s pr-4">{poiMessage.text}</div>
                  </div>
                </InfoWindow>
              )}

              {/* Opt-in contribution flow for a supported POI not yet in our DB.
                  Rendered as an AdvancedMarker (not an InfoWindow) so clicks inside
                  the interactive card don't leak to the map's onClick and close it.
                  Desktop: intro/unsupported are anchored on the map; the "What you
                  can do" options step is a centered modal (rendered below). */}
              {poiContribution && poiContribution.step !== "options" && (
                <AdvancedMarker
                  key="poi-contribution-card"
                  position={{ lat: poiContribution.lat, lng: poiContribution.lng }}
                  zIndex={3_000_000}
                  onClick={() => {}}
                >
                  <div className="hidden lg:block -translate-y-3 drop-shadow-xl">
                    <PoiContributionCard
                      step={poiContribution.step}
                      selected={poiContribution.selected}
                      creating={poiContribution.creating}
                      variant="card"
                      onShowOptions={handleContributionShowOptions}
                      onToggleOption={handleContributionToggleOption}
                      onContinue={handleContributionContinue}
                      onClose={() => setPoiContribution(null)}
                    />
                  </div>
                </AdvancedMarker>
              )}
            </VisGlMap>
          </APIProvider>
        ) : (
        <>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%",
            }}
            center={mapCenter}
            zoom={16}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions} // Pass the dynamic mapOptions here
            //onDragEnd={() => onMapMoved()}
            onClick={(e) => {
              // Intercept native Google POI clicks before the default clear.
              if (
                handlePoiClick(
                  e as google.maps.MapMouseEvent & {
                    placeId?: string;
                    stop?: () => void;
                  },
                )
              ) {
                return;
              }
              setActiveMarker(null);
              setSnippetPosition(null);
              setTooltipVenue(null);
              setHoveredMarkerId(null);
              setHoveredVenueId("");
              setSnippetOpen(false);
              handleClosePoi();
            }}
            onIdle={() => onMapMoved()}
          >
            {locations.map((location, index) => (
              <Marker
                ref={(marker) => {
                  if (marker) {
                    markerRefs.current.set(location.id, marker);
                  }
                }}
                key={`marker-${location.id}-${index}`}
                position={{
                  lat: location.latitude,
                  lng: location.longitude,
                }}
                // title={location.name || "Venue location"}
                zIndex={
                  selectedVenue && selectedVenue.id === location.id
                    ? 1000
                    : index
                }
                icon={{
                  url:
                    selectedVenue && selectedVenue.id === location.id
                      ? getSelectedPinIcon(location)
                      : getPinIcon(location),
                  anchor: new google.maps.Point(15, 30),
                  size:
                    selectedVenue && selectedVenue.id === location.id
                      ? new google.maps.Size(40, 40)
                      : hoveredMarkerId === location.id ||
                          hoveredVenueId === location.id ||
                          activeMarker === location.id
                        ? new google.maps.Size(42, 42)
                        : new google.maps.Size(30, 30),
                  scaledSize:
                    selectedVenue && selectedVenue.id === location.id
                      ? new google.maps.Size(40, 40)
                      : hoveredMarkerId === location.id ||
                          hoveredVenueId === location.id ||
                          activeMarker === location.id
                        ? new google.maps.Size(42, 42)
                        : new google.maps.Size(30, 30),
                }}
                onMouseOver={() => {
                  // If a snippet is already open, ignore all hover events
                  if (snippetOpen) {
                    return;
                  }
                  // Only show tooltip if no snippet is open
                  setTooltipVenue(location);
                  setHoveredVenueId(location.id);
                  setHoveredMarkerId(location.id);
                }}
                onMouseOut={() => {
                  // If a snippet is open, ignore all mouse out events
                  if (snippetOpen) {
                    return;
                  }
                  // Only clear states if no snippet is open
                  setTooltipVenue(null);
                  setHoveredVenueId("");
                  setHoveredMarkerId(null);
                }}
                onClick={() => {
                  // TODO: Panning check commented out since we're not panning
                  // if (isPanning) return;

                  // place_pin_selected — user tapped a search-result map pin
                  // (legacy Marker render path). Mirrors the AdvancedMarker handler.
                  trackEvent("place_pin_selected", {
                    brand_id: location.brandId ?? "",
                    brand_name: location.brandName ?? "",
                    business_id: location.business?.id?.toString() ?? "",
                    entry_index: index,
                    pin_type: pinTypeForVenue(location),
                    place_name: location?.name ?? "",
                  });

                  // On home page (currentVenueTypePage === 0): show snippet first, then user clicks snippet to load venue
                  // On venue details page (currentVenueTypePage === 1): directly load venue, skip snippet
                  if (
                    currentVenueTypePage === 1 ||
                    props.mapSource === "venuedetails"
                  ) {
                    handleLoadVenue(location);
                  } else {
                    handleShowSnippet(location.id);
                  }
                }}
              >
                {currentVenueTypePage === 0 &&
                !selectedVenue &&
                activeMarker === location.id &&
                markerRefs.current?.get(activeMarker) &&
                snippetPosition
                  ? (() => {
                      return (
                        <>
                          {/* Desktop: Show snippet near pin */}
                          <OverlayView
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            position={{
                              lat: location.latitude,
                              lng: location.longitude,
                            }}
                            getPixelPositionOffset={() =>
                              snippetPosition.offset
                            }
                          >
                            {/* Wrapper with pointer-events-none to not block map */}
                            <div
                              className={`hidden lg:block w-[300px] ${snippetPosition.className}`}
                              style={{ pointerEvents: "none" }}
                            >
                              <div
                                className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full"
                                style={{ pointerEvents: "auto" }}
                              >
                                <style>{`
                                  .map-snippet-wrapper button.bg-opacity-30 {
                                  right: 52px !important;
                                  }`}</style>

                                {/* Close button - positioned at right edge */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseSnippet();
                                  }}
                                  className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                                  aria-label="Close venue preview"
                                  type="button"
                                >
                                  <XIcon
                                    size={16}
                                    weight="bold"
                                    className="text-fg-neutral-strong"
                                  />
                                </button>

                                <button
                                  type="button"
                                  className="w-full text-left rounded p-0 focus-blue pointer-events-auto cursor-pointer"
                                  aria-label={`View details for ${location.name}`}
                                >
                                  <VenueCard
                                    location={location}
                                    onOpen={() => handleLoadVenue(location)}
                                    onSaveVenue={handleSaveVenue}
                                    savedPlaces={savedPlaces}
                                    truncateText={true}
                                    index={index}
                                    totalCount={locations.length}
                                    showMetaInfo={true}
                                    showOpeningHours={true}
                                    showVariantIndicators={true}
                                    showDescription={true}
                                    showAddress={false}
                                    inMapView={true}
                                  />
                                </button>
                              </div>
                            </div>
                          </OverlayView>
                        </>
                      );
                    })()
                  : null}
              </Marker>
            ))}
            {userLocation && (
              <Marker
                key={"userLocation"}
                position={{
                  lat: userLocation.latitude,
                  lng: userLocation.longitude,
                }}
                title={i18n.get("yourLocation") || "Your current location"}
                zIndex={10000}
                icon={{
                  url: YouAre,
                  anchor: new google.maps.Point(15, 30),
                  size: new google.maps.Size(30, 30),
                  scaledSize: new google.maps.Size(30, 30),
                }}
              ></Marker>
            )}

            {/* Venues resolved from Google POI clicks / suggest (persisted across clicks).
                Skip any that are already rendered as a normal search pin. */}
            {resolvedPois
              .filter(
                (v) =>
                  !locations.some(
                    (l) =>
                      (v.placeId && l.placeId === v.placeId) || l.id === v.id,
                  ),
              )
              .map((poiVenue) => (
              <Marker
                key={`poi-${poiVenue.id}`}
                position={{
                  lat: poiVenue.latitude,
                  lng: poiVenue.longitude,
                }}
                zIndex={1001}
                icon={{
                  url:
                    activeMarker === poiVenue.id
                      ? getSelectedPinIcon(poiVenue)
                      : getPinIcon(poiVenue),
                  anchor: new google.maps.Point(15, 30),
                  size: new google.maps.Size(40, 40),
                  scaledSize: new google.maps.Size(40, 40),
                }}
                onClick={() => {
                  // Mirror the regular-pin behaviour: on the venue-detail page
                  // load the venue directly (no snippet), on the search page show
                  // the snippet first.
                  if (
                    currentVenueTypePage === 1 ||
                    props.mapSource === "venuedetails"
                  ) {
                    handleLoadVenue(poiVenue);
                  } else {
                    setActiveMarker(poiVenue.id);
                    setSnippetPosition(getSnippetPosition(poiVenue));
                    setSnippetOpen(true);
                  }
                }}
              >
                {currentVenueTypePage === 0 &&
                !selectedVenue &&
                activeMarker === poiVenue.id &&
                snippetPosition ? (
                  <OverlayView
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    position={{
                      lat: poiVenue.latitude,
                      lng: poiVenue.longitude,
                    }}
                    getPixelPositionOffset={() => snippetPosition.offset}
                  >
                    <div
                      className={`hidden lg:block w-[300px] ${snippetPosition.className}`}
                      style={{ pointerEvents: "none" }}
                    >
                      <div
                        className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full"
                        style={{ pointerEvents: "auto" }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseSnippet();
                          }}
                          className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                          aria-label="Close venue preview"
                          type="button"
                        >
                          <XIcon
                            size={16}
                            weight="bold"
                            className="text-fg-neutral-strong"
                          />
                        </button>
                        <button
                          type="button"
                          className="w-full text-left rounded p-0 focus-blue pointer-events-auto cursor-pointer"
                          aria-label={`View details for ${poiVenue.name}`}
                        >
                          <VenueCard
                            location={poiVenue}
                            onOpen={() => handleLoadVenue(poiVenue)}
                            onSaveVenue={handleSaveVenue}
                            savedPlaces={savedPlaces}
                            truncateText={true}
                            index={0}
                            totalCount={1}
                            showMetaInfo={true}
                            showOpeningHours={true}
                            showVariantIndicators={true}
                            showDescription={true}
                            showAddress={false}
                            inMapView={true}
                          />
                        </button>
                      </div>
                    </div>
                  </OverlayView>
                ) : null}
              </Marker>
            ))}

            {/* Unsupported / error message card at the clicked point */}
            {poiMessage && (
              <OverlayView
                position={{ lat: poiMessage.lat, lng: poiMessage.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={(width, height) => ({
                  x: -(width / 2),
                  y: -height - 20,
                })}
              >
                <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-3 rounded-lg shadow-lg w-max max-w-[260px] relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPoiMessage(null);
                    }}
                    className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full hover:bg-bg-brand-ghost-hover focus-blue"
                    aria-label="Close"
                    type="button"
                  >
                    <XIcon size={14} weight="bold" />
                  </button>
                  <div className="body-s pr-4">{poiMessage.text}</div>
                </div>
              </OverlayView>
            )}

            {/* Hover tooltip - only show when no snippet is open */}
            {(() => {
              if (snippetOpen) return null; // Don't show tooltip when snippet is open
              const displayVenue =
                tooltipVenue ||
                (hoveredVenueId
                  ? locations.find((l) => l.id === hoveredVenueId)
                  : null);
              return displayVenue ? (
                <OverlayView
                  position={{
                    lat: displayVenue.latitude,
                    lng: displayVenue.longitude,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -height - 50,
                  })}
                >
                  <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-2 rounded-lg shadow-lg pointer-events-none w-max max-w-[400px]">
                    <div className="label-m-medium whitespace-nowrap">
                      {displayVenue.name}
                    </div>
                  </div>
                </OverlayView>
              ) : null;
            })()}
          </GoogleMap>

          {/* TODO: Invisible overlay commented out since we're not panning */}
          {/* {isPanning && (
<div
className="absolute inset-0 z-[1000] cursor-wait"
style={{ pointerEvents:'all' }}
/>
)} */}
        </>
        )
      )}
      {/* Resolving a tapped Google place: small non-blocking loading pill. */}
      {poiResolving && (
        <div className="absolute left-0 right-0 top-2 flex items-center justify-center pointer-events-none z-20">
          <div className="flex items-center gap-2 bg-bg-brand-secondary-default border border-border-token-neutral-minimal rounded-full px-4 py-2 shadow-lg">
            <span className="w-4 h-4 rounded-full border-2 border-fg-brand-subtle border-t-transparent animate-spin" />
            <span className="label-s-medium text-fg-neutral-strong">
              {i18n.get("loadingPlace")}
            </span>
          </div>
        </div>
      )}

      {/* Map mode toggle on the map (mobile + desktop). Lets users switch
          "Places with data" / "All places" here. Gated behind the POI-click
          feature flag. Hidden while a venue detail is open. */}
      {isSearchRoute &&
        enableGooglePoiClick &&
        !selectedVenue &&
        props.mapSource !== "venuedetails" && (
        <div className="absolute left-0 right-0 top-2 flex items-center justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <MapModeToggle
              mapMode={mapMode}
              onChange={changeMapMode}
              modes={
                enableAdvancedMarkers
                  ? ["sociability", "declutter", "all"]
                  : ["sociability", "all"]
              }
            />
          </div>
        </div>
      )}

      {/* Show nearby. Also available while a venue detail is open (small
          map) — re-centers the results on the current map viewport in
          place, without closing the open venue detail. */}
      {savedPlaceIdToView === "" && !poiResolving && (
        <div
          className={cn(
            "absolute left-0 right-0 flex items-center justify-center pointer-events-none",
            // Offset below the on-map mode toggle (all breakpoints) when it's shown.
            isSearchRoute && enableGooglePoiClick && !selectedVenue
              ? "top-16"
              : "top-2",
          )}
        >
          <StandardButton
            variant="secondary"
            size="auto"
            className="pointer-events-auto rounded-[8px] min-h-0 px-3 py-1.5 label-m-medium shadow-md"
            onClick={() => {
              // Get current map center
              const currentCenter = mapRef.getCenter();
              const currentLat = currentCenter.lat();
              const currentLng = currentCenter.lng();

              // Update mapCenter atom to keep it in sync
              // This ensures geocoding uses the correct location when user types a new search term
              setMapCenter({ lat: currentLat, lng: currentLng });

              const currentBounds = mapRef.getBounds();
              const currentNe = currentBounds?.getNorthEast();
              const currentSw = currentBounds?.getSouthWest();

              setSearchPageParams((prev) => {
                // Carry the active map mode as-is (sociability/declutter both
                // need mapping_status "mapped" to exclude grey/unmapped pins;
                // "all" — including when map_mode is unset — does not). Only
                // when the POI-click feature (and its toggle) is enabled.
                const currentMode = prev.map_mode ?? "sociability";
                const params = {
                  ...prev,
                  latitude: currentLat.toString(),
                  longitude: currentLng.toString(),
                  userlatitude: userLocation?.latitude.toString() || "",
                  userlongitude: userLocation?.longitude.toString() || "",
                  ...(enableGooglePoiClick
                    ? {
                        map_mode: currentMode,
                        mapping_status:
                          currentMode === "all" ? undefined : "mapped",
                      }
                    : {}),
                  searchThisAreaClicked: "yes",
                  autoZoom: "no", // user has chosen this viewport — never let fitBounds zoom out
                  zoom: mapRef?.getZoom().toString() || "15",
                  saved_place_id: savedPlaceIdToView,
                  saved_place_user_id: savedPlaceByUserIdToView,
                  saved_place_sortby: savedPlaceSortByToView,
                  unixTimestamp: Math.floor(Date.now() / 1000),
                  includeGoogleFallback: "false",
                  ...(currentNe && currentSw
                    ? {
                        map_ne_lat: currentNe.lat().toString(),
                        map_ne_lng: currentNe.lng().toString(),
                        map_sw_lat: currentSw.lat().toString(),
                        map_sw_lng: currentSw.lng().toString(),
                      }
                    : {
                        map_ne_lat: undefined,
                        map_ne_lng: undefined,
                        map_sw_lat: undefined,
                        map_sw_lng: undefined,
                      }),
                };
                // Clear expand_radius specifically — other search types (brand, venue_name, etc.) should persist
                if (prev.search_type === "expand_radius") {
                  delete params.search_type;
                  delete params.search_value;
                }
                return params;
              });
            }}
          >
            <span className="w-4 h-4 relative mr-1">
              <FiMapPin size={16} />
            </span>
            {searchPageParams.search
              ? i18n.get("searchThisArea")
              : i18n.get("showNearBy")}
          </StandardButton>
        </div>
      )}
      {/* Goto current location, +, - */}
      <div className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end pointer-events-none">
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            const currentZoom = mapRef.getZoom() ?? 0;
            if (currentZoom < 17) {
              const newZoom = currentZoom + 1;
              mapRef.setZoom(newZoom);
              // map_zoomed — in-map zoom-in control. BasicMapView is shared across
              // /search, saved-place detail & venue details (no single page scope),
              // and the event carries no page-distinguishing prop → fire the primitive.
              trackEvent("map_zoomed", {
                zoom_direction: "in",
                zoom_level: newZoom,
              });
            }
          }}
          className="p-2 mb-1 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("zoomIn") || "Zoom in"}
        >
          <FaPlus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            const currentZoom = mapRef.getZoom() ?? 0;
            if (currentZoom > 0) {
              const newZoom = currentZoom - 1;
              mapRef.setZoom(newZoom);
              // map_zoomed — in-map zoom-out control (mirrors the zoom-in handler).
              trackEvent("map_zoomed", {
                zoom_direction: "out",
                zoom_level: newZoom,
              });
            }
          }}
          className="p-2 mb-8 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={i18n.get("zoomOut") || "Zoom out"}
        >
          <FaMinus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            console.log("Requesting location");
            // map_location_centered — "go to current location" map control. Stub
            // payload; shared map widget → fire the primitive.
            trackEvent("map_location_centered", {});

            // Conditional logic based on locationError
            if (locationError) {
              setIsLocationModalOpen(true);
            } else {
              goToUserLocationRequestedRef.current = true;
              requestUserLocation();
            }
          }}
          className="shadow-md p-2 mb-4 mr-2 flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold pointer-events-auto"
          aria-label={
            i18n.get("goToCurrentLocation") || "Go to current location"
          }
        >
          <TbCurrentLocation
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
      </div>
      {(isLoading || locationLoading) && (
        <Loader
          includeGoogleFallback={
            searchPageParams.includeGoogleFallback === "true"
          }
        />
      )}

      {/* Mobile: Fixed bottom snippet */}
      {currentVenueTypePage === 0 &&
        !selectedVenue &&
        activeMarker &&
        (() => {
          // Include resolved POI venues (not part of `locations`).
          const location =
            resolvedPois.find((v) => v.id === activeMarker) ||
            locations.find((l) => l.id === activeMarker);
          const index = locations.findIndex((l) => l.id === activeMarker);
          if (!location) return null;

          return (
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up pointer-events-none">
              <style>{`
 @keyframes slide-up {
 from {
 transform: translateY(100%);
 }
 to {
 transform: translateY(0);
 }
 }
 .animate-slide-up {
 animation: slide-up 0.3s ease-out;
 }
 .mobile-snippet-wrapper button.bg-opacity-30 {
 right: 52px !important;
 }
 .mobile-snippet-container {
 max-height: 280px;
 overflow: hidden;
 }
 .mobile-snippet-wrapper figure {
 aspect-ratio: 16 / 7 !important;
 max-height: 140px !important;
 }
 .mobile-snippet-wrapper figure img {
 object-fit: cover !important;
 }`}</style>

              <div className="mobile-snippet-container relative">
                {/* Close button - positioned at right edge */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseSnippet();
                  }}
                  className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                  aria-label="Close venue preview"
                  type="button"
                >
                  <XIcon
                    size={16}
                    weight="bold"
                    className="text-fg-neutral-strong"
                  />
                </button>

                <button
                  type="button"
                  className="w-full text-left rounded p-0 focus-blue mobile-snippet-wrapper cursor-pointer pointer-events-auto"
                  aria-label={`View details for ${location.name}`}
                >
                  <VenueCard
                    location={location}
                    onOpen={() => handleLoadVenue(location)}
                    onSaveVenue={handleSaveVenue}
                    savedPlaces={savedPlaces}
                    truncateText={true}
                    index={index}
                    totalCount={locations.length}
                    showMetaInfo={true}
                    showOpeningHours={true}
                    showVariantIndicators={true}
                    showDescription={true}
                    showAddress={false}
                    inMapView={true}
                  />
                </button>
              </div>
            </div>
          );
        })()}

      {/* Desktop "What you can do" as a proper centered modal (intro/unsupported
          stay anchored on the map above). */}
      {poiContribution && poiContribution.step === "options" && (
        <div
          className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/40 p-4"
          onClick={() => setPoiContribution(null)}
        >
          <div
            className="drop-shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <PoiContributionCard
              step={poiContribution.step}
              selected={poiContribution.selected}
              creating={poiContribution.creating}
              variant="card"
              onShowOptions={handleContributionShowOptions}
              onToggleOption={handleContributionToggleOption}
              onContinue={handleContributionContinue}
              onClose={() => setPoiContribution(null)}
              onBack={handleContributionBack}
            />
          </div>
        </div>
      )}

      {/* Mobile contribution flow: bottom sheet for intro/unsupported, and a
          full-screen page for the "What you can do" options (desktop uses the
          centered modal above and the map-anchored card for other steps). */}
      {poiContribution && poiContribution.step === "options" && (
        <div className="lg:hidden fixed inset-0 z-[1100] bg-bg-brand-secondary-default">
          <PoiContributionCard
            step={poiContribution.step}
            selected={poiContribution.selected}
            creating={poiContribution.creating}
            variant="page"
            onShowOptions={handleContributionShowOptions}
            onToggleOption={handleContributionToggleOption}
            onContinue={handleContributionContinue}
            onClose={() => setPoiContribution(null)}
            onBack={handleContributionBack}
          />
        </div>
      )}

      {poiContribution && poiContribution.step !== "options" && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up">
          <style>{`
@keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
.animate-slide-up { animation: slide-up 0.3s ease-out; }`}</style>
          <PoiContributionCard
            step={poiContribution.step}
            selected={poiContribution.selected}
            creating={poiContribution.creating}
            variant="sheet"
            onShowOptions={handleContributionShowOptions}
            onToggleOption={handleContributionToggleOption}
            onContinue={handleContributionContinue}
            onClose={() => setPoiContribution(null)}
            onBack={handleContributionBack}
          />
        </div>
      )}

      <ModalWithAction
        isModalOpen={isLocationModalOpen}
        title={i18n.get("loctionSettingsRequired")}
        subtitle={i18n.get("loctionSettingsRequiredMessage")}
        cancelButtonTitle={""}
        actionButtonTitle={i18n.get("ok")}
        onClose={() => {
          setIsLocationModalOpen(false);
        }}
        onClick={handleSetLocation}
        type="default"
        actionButtonType="default"
      />

      {/* Saved Places Modal */}
      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={locations.find(
                (l) => l.business.id === locationForSavedPlaces,
              )}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={locationForSavedPlaces}
              venueDetails={locations.find(
                (l) => l.business.id === locationForSavedPlaces,
              )}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

export default BasicMapView;
